import { useState, useEffect, useRef, createContext, useContext, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Html5QrcodeScanner, Html5Qrcode } from "html5-qrcode";
import SignatureCanvas from "react-signature-canvas";
import { 
  Pill, LogOut, Plus, Camera, FileText, User, Building2, 
  Phone, Mail, MapPin, CreditCard, Calendar, Clock, Download, FileDown,
  ChevronRight, X, Check, Loader2, Upload, QrCode, ScanLine, Pen,
  Shield, Users, AlertTriangle, CheckCircle, XCircle, Eye, FileCheck, RefreshCw, Trash2,
  Settings, Save, Key
} from "lucide-react";

// Use relative URL - this works on any domain (preview or production)
// DO NOT use environment variable as it gets reset during deployment
const BACKEND_URL = '';
const API = '/api';

// Auth Context
const AuthContext = createContext(null);

const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const savedUser = localStorage.getItem("user");
    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
    }
    setLoading(false);
  }, []);

  const login = (token, userData) => {
    localStorage.setItem("token", token);
    localStorage.setItem("user", JSON.stringify(userData));
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// API helper with auth
const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// EU Countries
const EU_COUNTRIES = [
  { code: "AT", name: "Austria", flag: "🇦🇹" },
  { code: "BE", name: "Belgium", flag: "🇧🇪" },
  { code: "BG", name: "Bulgaria", flag: "🇧🇬" },
  { code: "HR", name: "Croatia", flag: "🇭🇷" },
  { code: "CY", name: "Cyprus", flag: "🇨🇾" },
  { code: "CZ", name: "Czech Republic", flag: "🇨🇿" },
  { code: "DK", name: "Denmark", flag: "🇩🇰" },
  { code: "EE", name: "Estonia", flag: "🇪🇪" },
  { code: "FI", name: "Finland", flag: "🇫🇮" },
  { code: "FR", name: "France", flag: "🇫🇷" },
  { code: "DE", name: "Germany", flag: "🇩🇪" },
  { code: "GR", name: "Greece", flag: "🇬🇷" },
  { code: "HU", name: "Hungary", flag: "🇭🇺" },
  { code: "IE", name: "Ireland", flag: "🇮🇪" },
  { code: "IT", name: "Italy", flag: "🇮🇹" },
  { code: "LV", name: "Latvia", flag: "🇱🇻" },
  { code: "LT", name: "Lithuania", flag: "🇱🇹" },
  { code: "LU", name: "Luxembourg", flag: "🇱🇺" },
  { code: "MT", name: "Malta", flag: "🇲🇹" },
  { code: "NL", name: "Netherlands", flag: "🇳🇱" },
  { code: "PL", name: "Poland", flag: "🇵🇱" },
  { code: "PT", name: "Portugal", flag: "🇵🇹" },
  { code: "RO", name: "Romania", flag: "🇷🇴" },
  { code: "SK", name: "Slovakia", flag: "🇸🇰" },
  { code: "SI", name: "Slovenia", flag: "🇸🇮" },
  { code: "ES", name: "Spain", flag: "🇪🇸" },
  { code: "SE", name: "Sweden", flag: "🇸🇪" },
];

// Landing Page
const LandingPage = () => {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white">
      <nav className="container mx-auto px-6 py-4 flex justify-between items-center">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 bg-emerald-700 rounded-xl flex items-center justify-center">
            <Pill className="w-6 h-6 text-white" />
          </div>
          <span className="text-xl font-bold text-emerald-900">EUMedTranslate</span>
        </div>
        <button
          data-testid="start-now-btn"
          onClick={() => navigate("/login")}
          className="px-6 py-2 bg-emerald-700 text-white rounded-full hover:bg-emerald-800 transition"
        >
          Sign In
        </button>
      </nav>

      <main className="container mx-auto px-6 py-20">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-emerald-600 font-medium mb-4">Cross-Border Healthcare</p>
          <h1 className="text-5xl font-bold text-gray-900 mb-6 leading-tight">
            Your prescription,<br />anywhere in EU
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            Seamlessly translate medical prescriptions between EU countries. 
            Get your medication wherever you travel, with full compliance to local standards.
          </p>
          <div className="flex gap-4 justify-center">
            <button
              data-testid="get-started-btn"
              onClick={() => navigate("/register")}
              className="px-8 py-3 bg-emerald-700 text-white rounded-full text-lg font-medium hover:bg-emerald-800 transition"
            >
              Start Now
            </button>
            <button
              onClick={() => navigate("/login")}
              className="px-8 py-3 border-2 border-emerald-700 text-emerald-700 rounded-full text-lg font-medium hover:bg-emerald-50 transition"
            >
              How it Works
            </button>
          </div>
        </div>

        <div className="mt-20 grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
            <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mb-4">
              <Camera className="w-6 h-6 text-emerald-700" />
            </div>
            <h3 className="text-lg font-semibold mb-2">Upload Prescription</h3>
            <p className="text-gray-600">Scan or manually enter your prescription from any EU country</p>
          </div>
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
            <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mb-4">
              <MapPin className="w-6 h-6 text-emerald-700" />
            </div>
            <h3 className="text-lg font-semibold mb-2">Select Destination</h3>
            <p className="text-gray-600">Choose where you need to use your prescription</p>
          </div>
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
            <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mb-4">
              <FileText className="w-6 h-6 text-emerald-700" />
            </div>
            <h3 className="text-lg font-semibold mb-2">Get Translation</h3>
            <p className="text-gray-600">Receive locally compliant prescription for any EU pharmacy</p>
          </div>
        </div>

        {/* Pricing Section - Patient-focused */}
        <div className="mt-20 max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Simple, Transparent Pricing</h2>
            <p className="text-gray-600 text-lg">Access your translated prescriptions instantly</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6 max-w-2xl mx-auto">
            {/* Single Prescription */}
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200 hover:border-emerald-300 transition">
              <div className="text-center">
                <p className="text-gray-500 text-sm font-medium mb-2">Pay Per Use</p>
                <div className="flex items-baseline justify-center gap-1 mb-4">
                  <span className="text-4xl font-bold text-gray-900">€4.99</span>
                  <span className="text-gray-500">/prescription</span>
                </div>
                <p className="text-gray-600 text-sm mb-4">Perfect for occasional travelers</p>
                <ul className="text-left space-y-2 text-sm text-gray-600">
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-600" />
                    Download & print translated PDF
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-600" />
                    Valid at any EU pharmacy
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-600" />
                    Lifetime access to this prescription
                  </li>
                </ul>
              </div>
            </div>
            
            {/* Monthly Subscription */}
            <div className="bg-gradient-to-br from-emerald-700 to-emerald-800 p-6 rounded-2xl shadow-lg text-white relative overflow-hidden">
              <div className="absolute top-3 right-3 bg-amber-400 text-amber-900 text-xs font-bold px-2 py-1 rounded-full">
                BEST VALUE
              </div>
              <div className="text-center">
                <p className="text-emerald-200 text-sm font-medium mb-2">Monthly Unlimited</p>
                <div className="flex items-baseline justify-center gap-1 mb-4">
                  <span className="text-4xl font-bold">€14.99</span>
                  <span className="text-emerald-200">/month</span>
                </div>
                <p className="text-emerald-100 text-sm mb-4">Ideal for frequent travelers & expats</p>
                <ul className="text-left space-y-2 text-sm text-emerald-100">
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-300" />
                    Unlimited prescription translations
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-300" />
                    All 27 EU countries included
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-300" />
                    Save up to 70% vs pay-per-use
                  </li>
                </ul>
              </div>
            </div>
          </div>
          
          <p className="text-center text-gray-500 text-sm mt-6">
            Viewing prescriptions is always free. Payment only required when downloading or printing.
          </p>
        </div>

        <div className="mt-16 text-center">
          <div className="inline-flex items-center gap-8 text-gray-500">
            <span className="font-semibold text-2xl text-emerald-700">27</span>
            <span>EU Countries Supported</span>
          </div>
        </div>
      </main>
    </div>
  );
};

// Login Page
const LoginPage = () => {
  const [userType, setUserType] = useState("patient");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Debug: show what's being sent
    console.log("Login attempt:", { email: email, password_length: password.length, user_type: userType });
    
    try {
      const res = await api.post("/auth/login", { email: email.trim().toLowerCase(), password, user_type: userType });
      login(res.data.token, res.data.user);
      toast.success("Welcome back!");
      
      if (userType === "admin") {
        navigate("/admin");
      } else if (userType === "doctor") {
        // Check doctor status
        const status = res.data.user.status;
        if (status === "pending") {
          navigate("/doctor/pending");
        } else if (status === "rejected") {
          navigate("/doctor/rejected");
        } else {
          navigate("/doctor");
        }
      } else {
        navigate("/patient");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-emerald-700 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Pill className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Welcome back</h1>
          <p className="text-gray-600">Sign in to your account</p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
          <div className="flex mb-6 bg-gray-100 rounded-xl p-1">
            <button
              data-testid="patient-tab"
              onClick={() => setUserType("patient")}
              className={`flex-1 py-2 rounded-lg text-sm font-medium transition ${
                userType === "patient" ? "bg-white shadow text-emerald-700" : "text-gray-600"
              }`}
            >
              Patient
            </button>
            <button
              data-testid="doctor-tab"
              onClick={() => setUserType("doctor")}
              className={`flex-1 py-2 rounded-lg text-sm font-medium transition ${
                userType === "doctor" ? "bg-white shadow text-emerald-700" : "text-gray-600"
              }`}
            >
              Doctor
            </button>
            <button
              data-testid="admin-tab"
              onClick={() => setUserType("admin")}
              className={`flex-1 py-2 rounded-lg text-sm font-medium transition ${
                userType === "admin" ? "bg-white shadow text-emerald-700" : "text-gray-600"
              }`}
            >
              Admin
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                data-testid="email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <input
                data-testid="password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="current-password"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                required
              />
            </div>
            <button
              data-testid="sign-in-btn"
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-emerald-700 text-white rounded-xl font-medium hover:bg-emerald-800 transition disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading && <Loader2 className="w-5 h-5 animate-spin" />}
              Sign In
            </button>
          </form>

          <div className="mt-4 text-center">
            <button 
              onClick={() => navigate("/forgot-password")} 
              className="text-sm text-emerald-700 hover:underline"
              data-testid="forgot-password-link"
            >
              Forgot your password?
            </button>
          </div>

          <p className="mt-4 text-center text-gray-600">
            Don't have an account?{" "}
            <button onClick={() => navigate("/register")} className="text-emerald-700 font-medium">
              Register
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

// Forgot Password Page
const ForgotPasswordPage = () => {
  const [userType, setUserType] = useState("patient");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email, user_type: userType });
      setSent(true);
      toast.success("If an account exists, a reset link has been sent!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send reset email");
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md text-center">
          <div className="w-16 h-16 bg-emerald-700 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Mail className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Check your email</h1>
          <p className="text-gray-600 mb-6">
            We've sent a password reset link to <strong>{email}</strong>
          </p>
          <p className="text-sm text-gray-500 mb-6">
            The link will expire in 1 hour. If you don't see the email, check your spam folder.
          </p>
          <button
            onClick={() => navigate("/login")}
            className="text-emerald-700 font-medium hover:underline"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-emerald-700 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Pill className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Forgot Password</h1>
          <p className="text-gray-600">Enter your email to reset your password</p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
          <div className="flex mb-6 bg-gray-100 rounded-xl p-1">
            <button
              onClick={() => setUserType("patient")}
              className={`flex-1 py-2 rounded-lg text-sm font-medium transition ${
                userType === "patient" ? "bg-white shadow text-emerald-700" : "text-gray-600"
              }`}
            >
              Patient
            </button>
            <button
              onClick={() => setUserType("doctor")}
              className={`flex-1 py-2 rounded-lg text-sm font-medium transition ${
                userType === "doctor" ? "bg-white shadow text-emerald-700" : "text-gray-600"
              }`}
            >
              Doctor
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                data-testid="forgot-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                required
              />
            </div>
            <button
              data-testid="send-reset-btn"
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-emerald-700 text-white rounded-xl font-medium hover:bg-emerald-800 transition disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading && <Loader2 className="w-5 h-5 animate-spin" />}
              Send Reset Link
            </button>
          </form>

          <p className="mt-6 text-center text-gray-600">
            Remember your password?{" "}
            <button onClick={() => navigate("/login")} className="text-emerald-700 font-medium">
              Sign In
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

// Reset Password Page
const ResetPasswordPage = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get token from URL query params
  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get("token");

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }
    
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setSuccess(true);
      toast.success("Password reset successful!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md text-center">
          <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <X className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Invalid Link</h1>
          <p className="text-gray-600 mb-6">
            This password reset link is invalid or has expired.
          </p>
          <button
            onClick={() => navigate("/forgot-password")}
            className="text-emerald-700 font-medium hover:underline"
          >
            Request a new reset link
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md text-center">
          <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8 text-emerald-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Password Reset!</h1>
          <p className="text-gray-600 mb-6">
            Your password has been successfully reset.
          </p>
          <button
            onClick={() => navigate("/login")}
            className="px-6 py-3 bg-emerald-700 text-white rounded-xl font-medium hover:bg-emerald-800 transition"
          >
            Sign In Now
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-emerald-700 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Pill className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Reset Password</h1>
          <p className="text-gray-600">Enter your new password</p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
              <input
                data-testid="new-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                required
                minLength={6}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
              <input
                data-testid="confirm-password-input"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                required
                minLength={6}
              />
            </div>
            <button
              data-testid="reset-password-btn"
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-emerald-700 text-white rounded-xl font-medium hover:bg-emerald-800 transition disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading && <Loader2 className="w-5 h-5 animate-spin" />}
              Reset Password
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

// Register Page
const RegisterPage = () => {
  const [userType, setUserType] = useState("patient");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  // Patient fields
  const [patientData, setPatientData] = useState({
    name: "", email: "", password: "", country: ""
  });

  // Doctor fields
  const [doctorData, setDoctorData] = useState({
    name: "", email: "", password: "", phone: "", country: "",
    license_number: "", clinic_name: "", clinic_address: "", specialty: ""
  });

  const handlePatientSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/auth/register/patient", patientData);
      login(res.data.token, res.data.user);
      toast.success("Account created successfully!");
      navigate("/patient");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const handleDoctorSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/auth/register/doctor", doctorData);
      login(res.data.token, res.data.user);
      toast.success("Account created successfully!");
      navigate("/doctor");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-emerald-700 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Pill className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Create account</h1>
          <p className="text-gray-600">Join EUMedTranslate today</p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
          <div className="flex mb-6 bg-gray-100 rounded-xl p-1">
            <button
              data-testid="register-patient-tab"
              onClick={() => setUserType("patient")}
              className={`flex-1 py-2 rounded-lg text-sm font-medium transition ${
                userType === "patient" ? "bg-white shadow text-emerald-700" : "text-gray-600"
              }`}
            >
              Patient
            </button>
            <button
              data-testid="register-doctor-tab"
              onClick={() => setUserType("doctor")}
              className={`flex-1 py-2 rounded-lg text-sm font-medium transition ${
                userType === "doctor" ? "bg-white shadow text-emerald-700" : "text-gray-600"
              }`}
            >
              Doctor
            </button>
          </div>

          {userType === "patient" ? (
            <form onSubmit={handlePatientSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                <input
                  data-testid="patient-name-input"
                  type="text"
                  value={patientData.name}
                  onChange={(e) => setPatientData({ ...patientData, name: e.target.value })}
                  placeholder="John Smith"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  data-testid="patient-email-input"
                  type="email"
                  value={patientData.email}
                  onChange={(e) => setPatientData({ ...patientData, email: e.target.value })}
                  placeholder="your@email.com"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                <input
                  data-testid="patient-password-input"
                  type="password"
                  value={patientData.password}
                  onChange={(e) => setPatientData({ ...patientData, password: e.target.value })}
                  placeholder="••••••••"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                <select
                  data-testid="patient-country-select"
                  value={patientData.country}
                  onChange={(e) => setPatientData({ ...patientData, country: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                  required
                >
                  <option value="">Select country</option>
                  {EU_COUNTRIES.map((c) => (
                    <option key={c.code} value={c.code}>{c.flag} {c.name}</option>
                  ))}
                </select>
              </div>
              <button
                data-testid="patient-register-btn"
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-emerald-700 text-white rounded-xl font-medium hover:bg-emerald-800 transition disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading && <Loader2 className="w-5 h-5 animate-spin" />}
                Create Account
              </button>
            </form>
          ) : (
            <form onSubmit={handleDoctorSubmit} className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                <input
                  data-testid="doctor-name-input"
                  type="text"
                  value={doctorData.name}
                  onChange={(e) => setDoctorData({ ...doctorData, name: e.target.value })}
                  placeholder="Dr. John Smith"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  data-testid="doctor-email-input"
                  type="email"
                  value={doctorData.email}
                  onChange={(e) => setDoctorData({ ...doctorData, email: e.target.value })}
                  placeholder="your@email.com"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                <input
                  data-testid="doctor-password-input"
                  type="password"
                  value={doctorData.password}
                  onChange={(e) => setDoctorData({ ...doctorData, password: e.target.value })}
                  placeholder="••••••••"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                <input
                  data-testid="doctor-phone-input"
                  type="tel"
                  value={doctorData.phone}
                  onChange={(e) => setDoctorData({ ...doctorData, phone: e.target.value })}
                  placeholder="+39 123 456 7890"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                <select
                  data-testid="doctor-country-select"
                  value={doctorData.country}
                  onChange={(e) => setDoctorData({ ...doctorData, country: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                  required
                >
                  <option value="">Select country</option>
                  {EU_COUNTRIES.map((c) => (
                    <option key={c.code} value={c.code}>{c.flag} {c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Medical License Number</label>
                <input
                  data-testid="doctor-license-input"
                  type="text"
                  value={doctorData.license_number}
                  onChange={(e) => setDoctorData({ ...doctorData, license_number: e.target.value })}
                  placeholder="MED-12345"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Clinic Name</label>
                <input
                  data-testid="doctor-clinic-name-input"
                  type="text"
                  value={doctorData.clinic_name}
                  onChange={(e) => setDoctorData({ ...doctorData, clinic_name: e.target.value })}
                  placeholder="City Medical Center"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Clinic Address</label>
                <input
                  data-testid="doctor-clinic-address-input"
                  type="text"
                  value={doctorData.clinic_address}
                  onChange={(e) => setDoctorData({ ...doctorData, clinic_address: e.target.value })}
                  placeholder="123 Main St, Rome, Italy"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Specialty (Optional)</label>
                <input
                  data-testid="doctor-specialty-input"
                  type="text"
                  value={doctorData.specialty}
                  onChange={(e) => setDoctorData({ ...doctorData, specialty: e.target.value })}
                  placeholder="General Practice"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                />
              </div>
              <button
                data-testid="doctor-register-btn"
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-emerald-700 text-white rounded-xl font-medium hover:bg-emerald-800 transition disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading && <Loader2 className="w-5 h-5 animate-spin" />}
                Create Account
              </button>
            </form>
          )}

          <p className="mt-6 text-center text-gray-600">
            Already have an account?{" "}
            <button onClick={() => navigate("/login")} className="text-emerald-700 font-medium">
              Sign In
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

// QR/Document Scanner Modal - Uses phone camera for real-time scanning
const ScanModal = ({ isOpen, onClose, onScanComplete }) => {
  const [scanMode, setScanMode] = useState("qr"); // "qr" or "document"
  const [scanning, setScanning] = useState(false);
  const [scannedData, setScannedData] = useState(null);
  const [imageData, setImageData] = useState(null);
  const [originCountry, setOriginCountry] = useState("");
  const [loading, setLoading] = useState(false);
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);
  const cameraInputRef = useRef(null);

  // Handle browser back button
  useEffect(() => {
    if (!isOpen) return;
    
    window.history.pushState({ modal: 'scan' }, '');
    
    const handlePopState = () => {
      onClose();
    };
    
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isOpen, onClose]);

  // Start QR Scanner
  const startQrScanner = useCallback(async () => {
    if (!scannerRef.current) return;
    
    try {
      const html5QrCode = new Html5Qrcode("qr-reader");
      html5QrCodeRef.current = html5QrCode;
      
      await html5QrCode.start(
        { facingMode: "environment" },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 }
        },
        (decodedText) => {
          // QR Code detected
          setScannedData(decodedText);
          setScanning(false);
          html5QrCode.stop().catch(console.error);
          toast.success("QR Code scanned successfully!");
        },
        (errorMessage) => {
          // Scan error - ignore, keep scanning
        }
      );
      setScanning(true);
    } catch (err) {
      console.error("QR Scanner error:", err);
      toast.error("Could not access camera. Please allow camera permissions.");
    }
  }, []);

  // Stop QR Scanner
  const stopQrScanner = useCallback(() => {
    if (html5QrCodeRef.current) {
      html5QrCodeRef.current.stop().catch(console.error);
      html5QrCodeRef.current = null;
    }
    setScanning(false);
  }, []);

  // Cleanup on close
  useEffect(() => {
    if (!isOpen) {
      stopQrScanner();
      setScannedData(null);
      setImageData(null);
      setOriginCountry("");
      setScanMode("qr");
    }
  }, [isOpen, stopQrScanner]);

  // Handle document photo capture
  const handleDocumentCapture = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setImageData(event.target.result);
        toast.success("Document captured!");
      };
      reader.readAsDataURL(file);
    }
  };

  // Submit scanned prescription
  const handleSubmit = async () => {
    if (!originCountry) {
      toast.error("Please select origin country");
      return;
    }

    if (scanMode === "qr" && !scannedData) {
      toast.error("Please scan a QR code first");
      return;
    }

    if (scanMode === "document" && !imageData) {
      toast.error("Please capture a document first");
      return;
    }

    setLoading(true);
    try {
      const res = await api.post("/prescriptions/scan", {
        image_data: scanMode === "document" ? imageData : null,
        qr_data: scanMode === "qr" ? scannedData : null,
        origin_country: originCountry,
        scan_type: scanMode
      });
      toast.success("Prescription created successfully!");
      onScanComplete(res.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to process scan");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">Scan Prescription</h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Scan Mode Tabs */}
          <div className="flex mb-4 bg-gray-100 rounded-xl p-1">
            <button
              data-testid="qr-mode-btn"
              onClick={() => { setScanMode("qr"); stopQrScanner(); setScannedData(null); }}
              className={`flex-1 py-2 rounded-lg text-sm font-medium transition flex items-center justify-center gap-2 ${
                scanMode === "qr" ? "bg-white shadow text-emerald-700" : "text-gray-600"
              }`}
            >
              <QrCode className="w-4 h-4" />
              QR Code
            </button>
            <button
              data-testid="doc-mode-btn"
              onClick={() => { setScanMode("document"); stopQrScanner(); setImageData(null); }}
              className={`flex-1 py-2 rounded-lg text-sm font-medium transition flex items-center justify-center gap-2 ${
                scanMode === "document" ? "bg-white shadow text-emerald-700" : "text-gray-600"
              }`}
            >
              <FileText className="w-4 h-4" />
              Document
            </button>
          </div>

          {/* QR Scanner Mode */}
          {scanMode === "qr" && (
            <div className="mb-4">
              {scannedData ? (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-center">
                  <Check className="w-12 h-12 text-emerald-600 mx-auto mb-2" />
                  <p className="font-medium text-emerald-800">QR Code Scanned!</p>
                  <p className="text-sm text-emerald-600 mt-1 break-all">{scannedData.substring(0, 50)}...</p>
                  <button
                    onClick={() => { setScannedData(null); }}
                    className="mt-3 text-sm text-emerald-700 underline"
                  >
                    Scan again
                  </button>
                </div>
              ) : (
                <>
                  <div 
                    id="qr-reader" 
                    ref={scannerRef}
                    className="w-full rounded-xl overflow-hidden bg-gray-900"
                    style={{ minHeight: scanning ? "300px" : "0" }}
                  />
                  {!scanning && (
                    <button
                      data-testid="start-qr-scan-btn"
                      onClick={startQrScanner}
                      className="w-full py-4 bg-emerald-700 text-white rounded-xl font-medium flex items-center justify-center gap-3 hover:bg-emerald-800 transition"
                    >
                      <ScanLine className="w-6 h-6" />
                      Start QR Scanner
                    </button>
                  )}
                  {scanning && (
                    <button
                      onClick={stopQrScanner}
                      className="w-full mt-3 py-2 border border-gray-300 rounded-xl text-sm text-gray-600"
                    >
                      Stop Scanner
                    </button>
                  )}
                </>
              )}
            </div>
          )}

          {/* Document Scanner Mode */}
          {scanMode === "document" && (
            <div className="mb-4">
              {imageData ? (
                <div className="text-center">
                  <img src={imageData} alt="Captured document" className="max-h-48 mx-auto rounded-xl mb-3" />
                  <button
                    onClick={() => setImageData(null)}
                    className="text-sm text-red-600 underline"
                  >
                    Remove and retake
                  </button>
                </div>
              ) : (
                <button
                  data-testid="capture-doc-btn"
                  onClick={() => cameraInputRef.current?.click()}
                  className="w-full py-6 border-2 border-dashed border-emerald-500 bg-emerald-50 rounded-xl flex flex-col items-center justify-center gap-2 hover:bg-emerald-100 transition"
                >
                  <Camera className="w-10 h-10 text-emerald-700" />
                  <span className="font-medium text-emerald-700">Tap to Open Camera</span>
                  <span className="text-sm text-emerald-600">Take photo of prescription</span>
                </button>
              )}
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleDocumentCapture}
                className="hidden"
              />
            </div>
          )}

          {/* Origin Country */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Origin Country</label>
            <select
              data-testid="scan-country-select"
              value={originCountry}
              onChange={(e) => setOriginCountry(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
            >
              <option value="">Select country</option>
              {EU_COUNTRIES.map((c) => (
                <option key={c.code} value={c.code}>{c.name}</option>
              ))}
            </select>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 py-3 border border-gray-300 rounded-xl font-medium hover:bg-gray-50 transition"
            >
              Cancel
            </button>
            <button
              data-testid="scan-submit-btn"
              onClick={handleSubmit}
              disabled={loading || !originCountry || (scanMode === "qr" && !scannedData) || (scanMode === "document" && !imageData)}
              className="flex-1 py-3 bg-emerald-700 text-white rounded-xl font-medium hover:bg-emerald-800 transition disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading && <Loader2 className="w-5 h-5 animate-spin" />}
              Create Prescription
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Upload Modal - For uploading existing files/digital documents
const UploadModal = ({ isOpen, onClose, onUploadComplete }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [originCountry, setOriginCountry] = useState("");
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);

  // Handle browser back button
  useEffect(() => {
    if (!isOpen) return;
    
    window.history.pushState({ modal: 'upload' }, '');
    
    const handlePopState = () => {
      onClose();
    };
    
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isOpen, onClose]);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      const reader = new FileReader();
      reader.onload = (event) => {
        setPreview(event.target.result);
      };
      reader.readAsDataURL(selectedFile);
    }
  };

  const handleUpload = async () => {
    if (!file || !originCountry) {
      toast.error("Please select a file and country");
      return;
    }

    setLoading(true);
    try {
      const res = await api.post("/prescriptions/scan", {
        image_data: preview,
        origin_country: originCountry,
        scan_type: "upload"
      });
      toast.success("Document uploaded successfully!");
      onUploadComplete(res.data);
      onClose();
      setFile(null);
      setPreview(null);
      setOriginCountry("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setFile(null);
      setPreview(null);
      setOriginCountry("");
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold">Upload Document</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* File Upload Area */}
        {preview ? (
          <div className="mb-4 text-center">
            <img src={preview} alt="Preview" className="max-h-48 mx-auto rounded-xl mb-3" />
            <p className="text-sm text-gray-600 mb-2">{file?.name}</p>
            <button
              onClick={() => { setFile(null); setPreview(null); }}
              className="text-sm text-red-600 underline"
            >
              Remove file
            </button>
          </div>
        ) : (
          <div
            data-testid="upload-area"
            onClick={() => fileInputRef.current?.click()}
            className="mb-4 border-2 border-dashed border-gray-300 rounded-xl p-8 text-center cursor-pointer hover:border-emerald-500 transition"
          >
            <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="font-medium text-gray-700">Click to upload</p>
            <p className="text-sm text-gray-500">PDF, JPG, PNG (max 10MB)</p>
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,.pdf"
          onChange={handleFileSelect}
          className="hidden"
        />

        {/* Origin Country */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">Origin Country</label>
          <select
            data-testid="upload-country-select"
            value={originCountry}
            onChange={(e) => setOriginCountry(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
          >
            <option value="">Select country</option>
            {EU_COUNTRIES.map((c) => (
              <option key={c.code} value={c.code}>{c.name}</option>
            ))}
          </select>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-3 border border-gray-300 rounded-xl font-medium hover:bg-gray-50 transition"
          >
            Cancel
          </button>
          <button
            data-testid="upload-submit-btn"
            onClick={handleUpload}
            disabled={loading || !file || !originCountry}
            className="flex-1 py-3 bg-emerald-700 text-white rounded-xl font-medium hover:bg-emerald-800 transition disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading && <Loader2 className="w-5 h-5 animate-spin" />}
            Upload
          </button>
        </div>
      </div>
    </div>
  );
};

// New Prescription Modal
const NewPrescriptionModal = ({ isOpen, onClose, onComplete }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    patient_name: "",
    patient_email: "",
    patient_dob: "",
    origin_country: user?.country || "",
    destination_country: "",
    notes: "",
    medications: [{ name: "", dosage: "", frequency: "", duration: "", quantity: "" }]
  });
  
  // Patient autocomplete state
  const [patientSuggestions, setPatientSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchingPatients, setSearchingPatients] = useState(false);
  const searchTimeoutRef = useRef(null);

  // Handle browser back button
  useEffect(() => {
    if (!isOpen) return;
    
    window.history.pushState({ modal: 'newPrescription' }, '');
    
    const handlePopState = () => {
      onClose();
    };
    
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isOpen, onClose]);

  // Search for previous patients when name changes
  const handlePatientNameChange = (name) => {
    setFormData({ ...formData, patient_name: name });
    
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    if (name.length < 2) {
      setPatientSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    
    // Debounce search
    searchTimeoutRef.current = setTimeout(async () => {
      setSearchingPatients(true);
      try {
        // Search both registered patients AND previous prescriptions
        const [registeredRes, previousRes] = await Promise.all([
          api.get(`/doctors/registered-patients/search?name=${encodeURIComponent(name)}`),
          api.get(`/doctors/patients/search?name=${encodeURIComponent(name)}`)
        ]);
        
        // Combine results, prioritizing registered patients
        const registered = registeredRes.data || [];
        const previous = previousRes.data || [];
        
        // Merge and deduplicate by email
        const seen = new Set();
        const combined = [];
        
        // Add registered patients first
        for (const p of registered) {
          if (p.patient_email && !seen.has(p.patient_email)) {
            seen.add(p.patient_email);
            combined.push({ ...p, source: 'registered' });
          }
        }
        
        // Add previous prescription patients
        for (const p of previous) {
          if (p.patient_email && !seen.has(p.patient_email)) {
            seen.add(p.patient_email);
            combined.push({ ...p, source: 'previous' });
          }
        }
        
        setPatientSuggestions(combined);
        setShowSuggestions(combined.length > 0);
      } catch (err) {
        console.error("Patient search failed:", err);
      } finally {
        setSearchingPatients(false);
      }
    }, 300);
  };

  // Select a patient from suggestions
  const selectPatient = (patient) => {
    setFormData({
      ...formData,
      patient_name: patient.patient_name,
      patient_email: patient.patient_email || "",
      patient_dob: patient.patient_dob || ""
    });
    setShowSuggestions(false);
    setPatientSuggestions([]);
  };

  const addMedication = () => {
    setFormData({
      ...formData,
      medications: [...formData.medications, { name: "", dosage: "", frequency: "", duration: "", quantity: "" }]
    });
  };

  const updateMedication = (index, field, value) => {
    const meds = [...formData.medications];
    meds[index][field] = value;
    setFormData({ ...formData, medications: meds });
  };

  const removeMedication = (index) => {
    if (formData.medications.length > 1) {
      const meds = formData.medications.filter((_, i) => i !== index);
      setFormData({ ...formData, medications: meds });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/prescriptions", formData);
      toast.success(res.data.email_notification_sent 
        ? "Prescription created and email sent to patient!" 
        : "Prescription created! (Email notification failed)");
      onComplete(res.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create prescription");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold">New Prescription</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-1">Patient Name</label>
              <input
                data-testid="new-rx-patient-name"
                type="text"
                value={formData.patient_name}
                onChange={(e) => handlePatientNameChange(e.target.value)}
                onFocus={() => patientSuggestions.length > 0 && setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                placeholder="Start typing to search..."
                autoComplete="off"
                required
              />
              {searchingPatients && (
                <div className="absolute right-3 top-9">
                  <Loader2 className="w-5 h-5 animate-spin text-emerald-600" />
                </div>
              )}
              {/* Patient suggestions dropdown */}
              {showSuggestions && patientSuggestions.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                  <div className="p-2 bg-emerald-50 text-xs text-emerald-700 font-medium border-b">
                    Patients found
                  </div>
                  {patientSuggestions.map((patient, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => selectPatient(patient)}
                      className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b last:border-b-0 flex justify-between items-center"
                    >
                      <div>
                        <div className="font-medium text-gray-900">{patient.patient_name}</div>
                        <div className="text-sm text-gray-500">{patient.patient_email}</div>
                      </div>
                      <div className="text-right">
                        {patient.patient_dob && (
                          <div className="text-xs text-gray-500">DOB: {patient.patient_dob}</div>
                        )}
                        {patient.source === 'registered' ? (
                          <div className="text-xs text-blue-600 font-medium">Registered Patient</div>
                        ) : (
                          <div className="text-xs text-emerald-600">{patient.prescription_count} prescription(s)</div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Patient Email</label>
              <input
                data-testid="new-rx-patient-email"
                type="email"
                value={formData.patient_email}
                onChange={(e) => setFormData({ ...formData, patient_email: e.target.value })}
                placeholder="patient@email.com"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                required
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
              <input
                data-testid="new-rx-patient-dob"
                type="date"
                value={formData.patient_dob}
                onChange={(e) => setFormData({ ...formData, patient_dob: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Origin Country</label>
              <select
                data-testid="new-rx-origin-country"
                value={formData.origin_country}
                onChange={(e) => setFormData({ ...formData, origin_country: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                required
              >
                <option value="">Select country</option>
                {EU_COUNTRIES.map((c) => (
                  <option key={c.code} value={c.code}>{c.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <div className="flex justify-between items-center mb-3">
              <label className="block text-sm font-medium text-gray-700">Medications</label>
              <button
                type="button"
                onClick={addMedication}
                className="text-sm text-emerald-700 font-medium flex items-center gap-1"
              >
                <Plus className="w-4 h-4" /> Add Medication
              </button>
            </div>
            {formData.medications.map((med, index) => (
              <div key={index} className="border border-gray-200 rounded-xl p-4 mb-3 bg-gray-50">
                <div className="flex justify-between items-center mb-3">
                  <span className="text-sm font-medium text-gray-500">Medication {index + 1}</span>
                  {formData.medications.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeMedication(index)}
                      className="p-1 text-red-500 hover:bg-red-100 rounded-lg text-xs flex items-center gap-1"
                    >
                      <X className="w-3 h-3" /> Remove
                    </button>
                  )}
                </div>
                
                {/* Medication Name - Full Width */}
                <div className="mb-3">
                  <label className="block text-xs text-gray-500 mb-1">Medication Name *</label>
                  <input
                    placeholder="e.g., Ibuprofen, Amoxicillin"
                    value={med.name}
                    onChange={(e) => updateMedication(index, "name", e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                    required
                  />
                </div>
                
                {/* Dosage and Quantity Row */}
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Dosage / Strength</label>
                    <input
                      placeholder="e.g., 400mg, 10ml"
                      value={med.dosage}
                      onChange={(e) => updateMedication(index, "dosage", e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Quantity / Amount</label>
                    <input
                      placeholder="e.g., 30 tablets, 1 box"
                      value={med.quantity}
                      onChange={(e) => updateMedication(index, "quantity", e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                    />
                  </div>
                </div>
                
                {/* Frequency and Duration Row */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Frequency</label>
                    <select
                      value={med.frequency}
                      onChange={(e) => updateMedication(index, "frequency", e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none bg-white"
                    >
                      <option value="">Select frequency</option>
                      <option value="Once daily">Once daily</option>
                      <option value="Twice daily">Twice daily</option>
                      <option value="3 times daily">3 times daily</option>
                      <option value="4 times daily">4 times daily</option>
                      <option value="Every 4 hours">Every 4 hours</option>
                      <option value="Every 6 hours">Every 6 hours</option>
                      <option value="Every 8 hours">Every 8 hours</option>
                      <option value="Every 12 hours">Every 12 hours</option>
                      <option value="Once weekly">Once weekly</option>
                      <option value="As needed">As needed (PRN)</option>
                      <option value="Other">Other (see notes)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Duration</label>
                    <select
                      value={med.duration || ""}
                      onChange={(e) => updateMedication(index, "duration", e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none bg-white"
                    >
                      <option value="">Select duration</option>
                      <option value="3 days">3 days</option>
                      <option value="5 days">5 days</option>
                      <option value="7 days">7 days</option>
                      <option value="10 days">10 days</option>
                      <option value="14 days">14 days</option>
                      <option value="21 days">21 days</option>
                      <option value="30 days">30 days</option>
                      <option value="60 days">60 days</option>
                      <option value="90 days">90 days</option>
                      <option value="Ongoing">Ongoing / Continuous</option>
                      <option value="Until finished">Until finished</option>
                      <option value="Other">Other (see notes)</option>
                    </select>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Additional Notes (Optional)</label>
            <textarea
              data-testid="new-rx-notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              placeholder="Special instructions, warnings, or additional information..."
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 border border-gray-300 rounded-xl font-medium hover:bg-gray-50 transition"
            >
              Cancel
            </button>
            <button
              data-testid="new-rx-submit-btn"
              type="submit"
              disabled={loading}
              className="flex-1 py-3 bg-emerald-700 text-white rounded-xl font-medium hover:bg-emerald-800 transition disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading && <Loader2 className="w-5 h-5 animate-spin" />}
              Create Prescription
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Edit Prescription Modal
const EditPrescriptionModal = ({ prescription, isOpen, onClose, onComplete }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    patient_name: "",
    patient_email: "",
    patient_dob: "",
    origin_country: "",
    destination_country: "",
    notes: "",
    medications: [{ name: "", dosage: "", frequency: "", duration: "", quantity: "" }]
  });

  // Handle browser back button
  useEffect(() => {
    if (!isOpen) return;
    
    window.history.pushState({ modal: 'editPrescription' }, '');
    
    const handlePopState = () => {
      onClose();
    };
    
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (prescription && isOpen) {
      setFormData({
        patient_name: prescription.patient_name || "",
        patient_email: prescription.patient_email || "",
        patient_dob: prescription.patient_dob || "",
        origin_country: prescription.origin_country || "",
        destination_country: prescription.destination_country || "",
        notes: prescription.notes || "",
        medications: prescription.medications?.length > 0 
          ? prescription.medications 
          : [{ name: "", dosage: "", frequency: "", duration: "", quantity: "" }]
      });
    }
  }, [prescription, isOpen]);

  const addMedication = () => {
    setFormData({
      ...formData,
      medications: [...formData.medications, { name: "", dosage: "", frequency: "", duration: "", quantity: "" }]
    });
  };

  const updateMedication = (index, field, value) => {
    const meds = [...formData.medications];
    meds[index][field] = value;
    setFormData({ ...formData, medications: meds });
  };

  const removeMedication = (index) => {
    if (formData.medications.length > 1) {
      const meds = formData.medications.filter((_, i) => i !== index);
      setFormData({ ...formData, medications: meds });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.put(`/prescriptions/${prescription.id}`, formData);
      onComplete(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update prescription");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !prescription) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-xl font-bold">Edit Prescription</h2>
            <p className="text-sm text-gray-500">{prescription.prescription_number}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Patient Name</label>
              <input
                type="text"
                value={formData.patient_name}
                onChange={(e) => setFormData({ ...formData, patient_name: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Patient Email</label>
              <input
                type="email"
                value={formData.patient_email}
                onChange={(e) => setFormData({ ...formData, patient_email: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
              <input
                type="date"
                value={formData.patient_dob}
                onChange={(e) => setFormData({ ...formData, patient_dob: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Origin Country</label>
              <select
                value={formData.origin_country}
                onChange={(e) => setFormData({ ...formData, origin_country: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                required
              >
                <option value="">Select country</option>
                {EU_COUNTRIES.map((c) => (
                  <option key={c.code} value={c.code}>{c.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <div className="flex justify-between items-center mb-3">
              <label className="block text-sm font-medium text-gray-700">Medications</label>
              <button
                type="button"
                onClick={addMedication}
                className="text-sm text-emerald-700 font-medium flex items-center gap-1"
              >
                <Plus className="w-4 h-4" /> Add Medication
              </button>
            </div>
            {formData.medications.map((med, index) => (
              <div key={index} className="border border-gray-200 rounded-xl p-4 mb-3 bg-gray-50">
                <div className="flex justify-between items-center mb-3">
                  <span className="text-sm font-medium text-gray-500">Medication {index + 1}</span>
                  {formData.medications.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeMedication(index)}
                      className="p-1 text-red-500 hover:bg-red-100 rounded-lg text-xs flex items-center gap-1"
                    >
                      <X className="w-3 h-3" /> Remove
                    </button>
                  )}
                </div>
                
                <div className="mb-3">
                  <label className="block text-xs text-gray-500 mb-1">Medication Name *</label>
                  <input
                    placeholder="e.g., Ibuprofen, Amoxicillin"
                    value={med.name}
                    onChange={(e) => updateMedication(index, "name", e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                    required
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Dosage</label>
                    <input
                      placeholder="e.g., 400mg"
                      value={med.dosage || ""}
                      onChange={(e) => updateMedication(index, "dosage", e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Quantity</label>
                    <input
                      placeholder="e.g., 30 tablets"
                      value={med.quantity || ""}
                      onChange={(e) => updateMedication(index, "quantity", e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Frequency</label>
                    <select
                      value={med.frequency || ""}
                      onChange={(e) => updateMedication(index, "frequency", e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none bg-white"
                    >
                      <option value="">Select frequency</option>
                      <option value="Once daily">Once daily</option>
                      <option value="Twice daily">Twice daily</option>
                      <option value="3 times daily">3 times daily</option>
                      <option value="4 times daily">4 times daily</option>
                      <option value="Every 4 hours">Every 4 hours</option>
                      <option value="Every 6 hours">Every 6 hours</option>
                      <option value="Every 8 hours">Every 8 hours</option>
                      <option value="Every 12 hours">Every 12 hours</option>
                      <option value="Once weekly">Once weekly</option>
                      <option value="As needed">As needed (PRN)</option>
                      <option value="Other">Other (see notes)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Duration</label>
                    <select
                      value={med.duration || ""}
                      onChange={(e) => updateMedication(index, "duration", e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none bg-white"
                    >
                      <option value="">Select duration</option>
                      <option value="3 days">3 days</option>
                      <option value="5 days">5 days</option>
                      <option value="7 days">7 days</option>
                      <option value="10 days">10 days</option>
                      <option value="14 days">14 days</option>
                      <option value="21 days">21 days</option>
                      <option value="30 days">30 days</option>
                      <option value="60 days">60 days</option>
                      <option value="90 days">90 days</option>
                      <option value="Ongoing">Ongoing / Continuous</option>
                      <option value="Until finished">Until finished</option>
                      <option value="Other">Other (see notes)</option>
                    </select>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              placeholder="Special instructions..."
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 border border-gray-300 rounded-xl font-medium hover:bg-gray-50 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-3 bg-emerald-700 text-white rounded-xl font-medium hover:bg-emerald-800 transition disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading && <Loader2 className="w-5 h-5 animate-spin" />}
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Renew Prescription Modal - Creates a new prescription based on an existing one
const RenewPrescriptionModal = ({ prescription, isOpen, onClose, onComplete }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    patient_name: "",
    patient_email: "",
    patient_dob: "",
    origin_country: "",
    destination_country: "",
    notes: "",
    medications: [{ name: "", dosage: "", frequency: "", duration: "", quantity: "" }]
  });

  // Handle browser back button
  useEffect(() => {
    if (!isOpen) return;
    
    window.history.pushState({ modal: 'renewPrescription' }, '');
    
    const handlePopState = () => {
      onClose();
    };
    
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (prescription && isOpen) {
      setFormData({
        patient_name: prescription.patient_name || "",
        patient_email: prescription.patient_email || "",
        patient_dob: prescription.patient_dob || "",
        origin_country: prescription.origin_country || user?.country || "",
        destination_country: prescription.destination_country || "",
        notes: prescription.notes || "",
        medications: prescription.medications?.length > 0 
          ? prescription.medications.map(m => ({...m}))
          : [{ name: "", dosage: "", frequency: "", duration: "", quantity: "" }]
      });
    }
  }, [prescription, isOpen, user]);

  const addMedication = () => {
    setFormData({
      ...formData,
      medications: [...formData.medications, { name: "", dosage: "", frequency: "", duration: "", quantity: "" }]
    });
  };

  const updateMedication = (index, field, value) => {
    const meds = [...formData.medications];
    meds[index][field] = value;
    setFormData({ ...formData, medications: meds });
  };

  const removeMedication = (index) => {
    if (formData.medications.length > 1) {
      const meds = formData.medications.filter((_, i) => i !== index);
      setFormData({ ...formData, medications: meds });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Create a NEW prescription (not update)
      const res = await api.post('/prescriptions', formData);
      toast.success(res.data.email_notification_sent 
        ? "Prescription renewed and email sent to patient!" 
        : "Prescription renewed! (Email notification failed)");
      onComplete(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to renew prescription");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !prescription) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-xl font-bold text-emerald-700">Renew Prescription</h2>
            <p className="text-sm text-gray-500">Based on: {prescription.prescription_number}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-4">
          <p className="text-sm text-emerald-700">
            This will create a <strong>new prescription</strong> with a new prescription number. 
            The patient will receive an email notification.
          </p>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Patient Name</label>
              <input
                type="text"
                value={formData.patient_name}
                onChange={(e) => setFormData({ ...formData, patient_name: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Patient Email</label>
              <input
                type="email"
                value={formData.patient_email}
                onChange={(e) => setFormData({ ...formData, patient_email: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                required
              />
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
              <input
                type="date"
                value={formData.patient_dob}
                onChange={(e) => setFormData({ ...formData, patient_dob: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Origin Country</label>
              <select
                value={formData.origin_country}
                onChange={(e) => setFormData({ ...formData, origin_country: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none bg-white"
                required
              >
                <option value="">Select country</option>
                {EU_COUNTRIES.map(c => (
                  <option key={c.code} value={c.code}>{c.flag} {c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Destination Country</label>
              <select
                value={formData.destination_country}
                onChange={(e) => setFormData({ ...formData, destination_country: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none bg-white"
              >
                <option value="">Select country (optional)</option>
                {EU_COUNTRIES.map(c => (
                  <option key={c.code} value={c.code}>{c.flag} {c.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Medications */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium text-gray-700">Medications</label>
              <button
                type="button"
                onClick={addMedication}
                className="text-sm text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-1"
              >
                <Plus className="w-4 h-4" /> Add medication
              </button>
            </div>
            
            {formData.medications.map((med, index) => (
              <div key={index} className="p-4 border border-gray-200 rounded-xl mb-3 relative">
                {formData.medications.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeMedication(index)}
                    className="absolute top-2 right-2 p-1 text-red-500 hover:bg-red-50 rounded"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
                
                <div className="mb-3">
                  <label className="block text-xs text-gray-500 mb-1">Medication Name</label>
                  <input
                    placeholder="e.g., Ibuprofen 400mg"
                    value={med.name || ""}
                    onChange={(e) => updateMedication(index, "name", e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                    required
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Dosage</label>
                    <input
                      placeholder="e.g., 400mg"
                      value={med.dosage || ""}
                      onChange={(e) => updateMedication(index, "dosage", e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Quantity</label>
                    <input
                      placeholder="e.g., 30 tablets"
                      value={med.quantity || ""}
                      onChange={(e) => updateMedication(index, "quantity", e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Frequency</label>
                    <select
                      value={med.frequency || ""}
                      onChange={(e) => updateMedication(index, "frequency", e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none bg-white"
                    >
                      <option value="">Select frequency</option>
                      <option value="Once daily">Once daily</option>
                      <option value="Twice daily">Twice daily</option>
                      <option value="3 times daily">3 times daily</option>
                      <option value="4 times daily">4 times daily</option>
                      <option value="Every 4 hours">Every 4 hours</option>
                      <option value="Every 6 hours">Every 6 hours</option>
                      <option value="Every 8 hours">Every 8 hours</option>
                      <option value="Every 12 hours">Every 12 hours</option>
                      <option value="Once weekly">Once weekly</option>
                      <option value="As needed">As needed (PRN)</option>
                      <option value="Other">Other (see notes)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Duration</label>
                    <select
                      value={med.duration || ""}
                      onChange={(e) => updateMedication(index, "duration", e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none bg-white"
                    >
                      <option value="">Select duration</option>
                      <option value="3 days">3 days</option>
                      <option value="5 days">5 days</option>
                      <option value="7 days">7 days</option>
                      <option value="10 days">10 days</option>
                      <option value="14 days">14 days</option>
                      <option value="21 days">21 days</option>
                      <option value="30 days">30 days</option>
                      <option value="60 days">60 days</option>
                      <option value="90 days">90 days</option>
                      <option value="Ongoing">Ongoing</option>
                    </select>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes / Instructions</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none resize-none"
              placeholder="Additional instructions for the patient..."
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 border border-gray-300 rounded-xl font-medium hover:bg-gray-50 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-3 bg-emerald-700 text-white rounded-xl font-medium hover:bg-emerald-800 transition disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading && <Loader2 className="w-5 h-5 animate-spin" />}
              Create Renewed Prescription
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};


// Payment Modal Component
const PaymentModal = ({ isOpen, onClose, prescriptionId, onPaymentSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState("single_prescription");

  const handlePayment = async () => {
    setLoading(true);
    try {
      const res = await api.post('/payments/create-checkout', {
        package_type: selectedPackage,
        prescription_id: prescriptionId
      });
      
      // Redirect to Stripe checkout
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error("Failed to start payment. Please try again.");
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-900">Access Prescription</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <p className="text-gray-600 mb-6">
          To download, print, or save this prescription, please choose a payment option:
        </p>
        
        <div className="space-y-3 mb-6">
          {/* Single prescription option */}
          <button
            onClick={() => setSelectedPackage("single_prescription")}
            className={`w-full p-4 border-2 rounded-xl text-left transition ${
              selectedPackage === "single_prescription" 
                ? "border-emerald-500 bg-emerald-50" 
                : "border-gray-200 hover:border-gray-300"
            }`}
          >
            <div className="flex justify-between items-center">
              <div>
                <div className="font-semibold text-gray-900">Single Prescription</div>
                <div className="text-sm text-gray-500">Access this prescription only</div>
              </div>
              <div className="text-2xl font-bold text-emerald-700">€4.99</div>
            </div>
          </button>
          
          {/* Subscription option */}
          <button
            onClick={() => setSelectedPackage("monthly_subscription")}
            className={`w-full p-4 border-2 rounded-xl text-left transition ${
              selectedPackage === "monthly_subscription" 
                ? "border-emerald-500 bg-emerald-50" 
                : "border-gray-200 hover:border-gray-300"
            }`}
          >
            <div className="flex justify-between items-center">
              <div>
                <div className="font-semibold text-gray-900">
                  Monthly Unlimited
                  <span className="ml-2 px-2 py-0.5 text-xs bg-amber-100 text-amber-700 rounded-full">BEST VALUE</span>
                </div>
                <div className="text-sm text-gray-500">Unlimited prescriptions for 30 days</div>
              </div>
              <div className="text-2xl font-bold text-emerald-700">€14.99</div>
            </div>
          </button>
        </div>
        
        <button
          onClick={handlePayment}
          disabled={loading}
          className="w-full py-3 bg-emerald-700 text-white rounded-xl font-semibold hover:bg-emerald-800 transition disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Processing...
            </>
          ) : (
            <>
              <CreditCard className="w-5 h-5" />
              Pay Now
            </>
          )}
        </button>
        
        <p className="text-xs text-gray-400 text-center mt-4">
          Secure payment powered by Stripe. All major cards accepted.
        </p>
      </div>
    </div>
  );
};




// Prescription Receipt Component (Invoice-style)
const PrescriptionReceipt = ({ prescription, onClose, onUpdate, onEdit, onRenew }) => {
  const { user } = useAuth();
  const [signing, setSigning] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [currentPrescription, setCurrentPrescription] = useState(prescription);
  const [hasAccess, setHasAccess] = useState(user?.user_type === 'doctor');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(user?.user_type === 'patient');
  
  // Check access for patients
  useEffect(() => {
    const checkAccess = async () => {
      if (user?.user_type === 'patient') {
        try {
          const res = await api.get(`/payments/access-check/${prescription.id}`);
          setHasAccess(res.data.has_access);
        } catch (err) {
          setHasAccess(false);
        } finally {
          setCheckingAccess(false);
        }
      }
    };
    checkAccess();
  }, [prescription.id, user]);
  
  // Handle browser back button
  useEffect(() => {
    // Push a state to history when modal opens
    window.history.pushState({ modal: 'prescription' }, '');
    
    const handlePopState = (event) => {
      // When back is pressed, close the modal instead of navigating
      onClose();
    };
    
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [onClose]);
  
  const getCountryName = (code) => {
    const country = EU_COUNTRIES.find(c => c.code === code);
    return country ? `${country.flag} ${country.name}` : code;
  };

  // Check access before performing action (for patients)
  const checkAccessAndPerform = (action) => {
    if (user?.user_type === 'doctor' || hasAccess) {
      action();
    } else {
      setShowPaymentModal(true);
    }
  };

  const handlePrint = () => {
    checkAccessAndPerform(() => {
      // Get the receipt content
      const receiptContent = document.getElementById('prescription-receipt');
      if (!receiptContent) {
        toast.error("Could not find prescription content to print");
        return;
      }
      
      // Create a new window for printing (works better on mobile)
      const printWindow = window.open('', '_blank', 'width=800,height=600');
      if (!printWindow) {
        // Fallback to regular print if popup blocked
        window.print();
        return;
      }
    
      // Write the print document with inline styles
      printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Prescription - ${currentPrescription.prescription_number}</title>
        <style>
          * { box-sizing: border-box; margin: 0; padding: 0; }
          body { font-family: -apple-system, BlinkMacSystemFont, sans-serif; padding: 20px; background: white; }
          .border-2 { border: 2px solid #1f2937; }
          .border { border: 1px solid #d1d5db; }
          .rounded-xl { border-radius: 12px; }
          .rounded-lg { border-radius: 8px; }
          .p-6 { padding: 24px; }
          .p-4 { padding: 16px; }
          .p-3 { padding: 12px; }
          .mb-6 { margin-bottom: 24px; }
          .mb-4 { margin-bottom: 16px; }
          .mb-3 { margin-bottom: 12px; }
          .mb-2 { margin-bottom: 8px; }
          .mt-1 { margin-top: 4px; }
          .mt-8 { margin-top: 32px; }
          .pt-4 { padding-top: 16px; }
          .flex { display: flex; }
          .justify-between { justify-content: space-between; }
          .items-start { align-items: flex-start; }
          .items-center { align-items: center; }
          .gap-4 { gap: 16px; }
          .gap-2 { gap: 8px; }
          .grid { display: grid; }
          .grid-cols-2 { grid-template-columns: repeat(2, 1fr); gap: 16px; }
          .space-y-2 > * + * { margin-top: 8px; }
          .text-center { text-align: center; }
          .text-right { text-align: right; }
          .text-xl { font-size: 20px; }
          .text-lg { font-size: 18px; }
          .text-sm { font-size: 14px; }
          .text-xs { font-size: 12px; }
          .font-bold { font-weight: 700; }
          .font-semibold { font-weight: 600; }
          .font-medium { font-weight: 500; }
          .text-gray-900 { color: #111827; }
          .text-gray-700 { color: #374151; }
          .text-gray-600 { color: #4b5563; }
          .text-gray-500 { color: #6b7280; }
          .text-emerald-700 { color: #047857; }
          .text-white { color: white; }
          .bg-white { background: white; }
          .bg-gray-50 { background: #f9fafb; }
          .bg-gray-100 { background: #f3f4f6; }
          .bg-emerald-700 { background: #047857; color: white; }
          .uppercase { text-transform: uppercase; }
          .border-t { border-top: 1px solid #d1d5db; }
          .border-t-2 { border-top: 2px solid #1f2937; }
          .border-b { border-bottom: 1px solid #d1d5db; }
          img { max-width: 200px; height: auto; }
          table { width: 100%; border-collapse: collapse; }
          th, td { padding: 8px 12px; text-align: left; border: 1px solid #d1d5db; }
          th { background: #047857; color: white; }
          @media print { body { padding: 10px; } }
        </style>
      </head>
      <body>
        ${receiptContent.innerHTML}
      </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    
    // Give time for content to render, then print
    setTimeout(() => {
      printWindow.print();
      setTimeout(() => printWindow.close(), 500);
    }, 250);
    });
  };

  const handleDownloadPDF = async () => {
    checkAccessAndPerform(async () => {
      setDownloading(true);
      try {
        const response = await api.get(`/prescriptions/${currentPrescription.id}/pdf`, {
          responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `prescription_${currentPrescription.prescription_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("PDF downloaded successfully!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to download PDF");
    } finally {
      setDownloading(false);
    }
    });
  };

  const handleSign = async () => {
    setSigning(true);
    try {
      const res = await api.post(`/prescriptions/${currentPrescription.id}/sign`);
      setCurrentPrescription(res.data);
      if (onUpdate) onUpdate(res.data);
      toast.success("Prescription signed successfully!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to sign prescription");
    } finally {
      setSigning(false);
    }
  };

  const isDoctor = user?.user_type === "doctor";
  const canSign = isDoctor && !currentPrescription.doctor_signature;

  if (!prescription) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 print:bg-white print:p-0">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto print:max-h-none print:overflow-visible print:rounded-none printable-modal">
        <div className="p-6 border-b flex justify-between items-center no-print">
          <h2 className="text-xl font-bold">Prescription Receipt</h2>
          <div className="flex gap-2 flex-wrap">
            {user?.user_type === 'doctor' && onEdit && (
              <button
                onClick={() => { onClose(); onEdit(currentPrescription); }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg flex items-center gap-2 hover:bg-blue-700"
              >
                <Pen className="w-4 h-4" />
                Edit
              </button>
            )}
            {user?.user_type === 'doctor' && onRenew && (
              <button
                onClick={() => { onClose(); onRenew(currentPrescription); }}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg flex items-center gap-2 hover:bg-emerald-700"
              >
                <RefreshCw className="w-4 h-4" />
                Renew
              </button>
            )}
            {canSign && (
              <button
                data-testid="sign-prescription-btn"
                onClick={handleSign}
                disabled={signing}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg flex items-center gap-2 hover:bg-purple-700 disabled:opacity-50"
              >
                {signing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Pen className="w-4 h-4" />}
                Sign
              </button>
            )}
            <button
              data-testid="download-pdf-btn"
              onClick={handleDownloadPDF}
              disabled={downloading}
              className="px-4 py-2 bg-amber-600 text-white rounded-lg flex items-center gap-2 hover:bg-amber-700 disabled:opacity-50"
            >
              {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
              PDF
            </button>
            <button
              onClick={handlePrint}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg flex items-center gap-2"
            >
              <Download className="w-4 h-4" /> Print
            </button>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Invoice-style Receipt */}
        <div className="p-8 print:p-4" id="prescription-receipt">
          {/* Header */}
          <div className="border-2 border-gray-800 p-6 mb-6">
            <div className="flex justify-between items-start">
              {/* Clinic Info (Left) */}
              <div className="flex gap-4">
                <div className="w-16 h-16 bg-emerald-700 rounded-xl flex items-center justify-center">
                  <Pill className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-900">{prescription.clinic_name || "Medical Clinic"}</h1>
                  <p className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                    <MapPin className="w-4 h-4" />
                    {prescription.clinic_address || "Address not provided"}
                  </p>
                  <p className="text-sm text-gray-600 flex items-center gap-1">
                    <Phone className="w-4 h-4" />
                    {prescription.doctor_phone || "Phone not provided"}
                  </p>
                  <p className="text-sm text-gray-600 flex items-center gap-1">
                    <Mail className="w-4 h-4" />
                    {prescription.doctor_email || "Email not provided"}
                  </p>
                </div>
              </div>

              {/* Receipt Info (Right) */}
              <div className="text-right">
                <div className="bg-gray-100 px-4 py-2 rounded mb-2">
                  <p className="text-xs text-gray-500">PRESCRIPTION NO.</p>
                  <p className="font-bold text-lg">{prescription.prescription_number}</p>
                </div>
                <div className="text-sm text-gray-600">
                  <p>Date: {new Date(prescription.created_at).toLocaleDateString()}</p>
                  <p>Origin: {getCountryName(prescription.origin_country)}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Doctor & Patient Info */}
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            {/* Doctor Info */}
            <div className="border border-gray-300 rounded-lg p-4">
              <h3 className="text-xs font-semibold text-gray-500 uppercase mb-3 border-b pb-2">Prescribing Doctor</h3>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-gray-400" />
                  <span className="font-medium">{prescription.doctor_name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-gray-400" />
                  <span className="text-sm">License: {prescription.doctor_license || "N/A"}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-gray-400" />
                  <span className="text-sm">{prescription.doctor_email}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-gray-400" />
                  <span className="text-sm">{prescription.doctor_phone || "N/A"}</span>
                </div>
              </div>
            </div>

            {/* Patient Info */}
            <div className="border border-gray-300 rounded-lg p-4">
              <h3 className="text-xs font-semibold text-gray-500 uppercase mb-3 border-b pb-2">Patient Information</h3>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-gray-400" />
                  <span className="font-medium">{prescription.patient_name}</span>
                </div>
                {prescription.patient_dob && (
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <span className="text-sm">DOB: {prescription.patient_dob}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Medications - Mobile-friendly card layout */}
          <div className="mb-6">
            <h3 className="text-xs font-semibold text-gray-500 uppercase mb-3">Medications</h3>
            {prescription.medications?.map((med, index) => (
              <div key={index} className="border border-gray-300 rounded-lg p-3 mb-2 bg-gray-50">
                <div className="flex justify-between items-start mb-2">
                  <span className="font-bold text-gray-900">{med.name}</span>
                  <span className="text-xs bg-gray-200 px-2 py-1 rounded">#{String(index + 1).padStart(2, '0')}</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-gray-500">Dosage:</span>
                    <span className="ml-1 font-medium">{med.dosage || "-"}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Qty:</span>
                    <span className="ml-1 font-medium">{med.quantity || "-"}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Frequency:</span>
                    <span className="ml-1 font-medium">{med.frequency || "-"}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Duration:</span>
                    <span className="ml-1 font-medium">{med.duration || "-"}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Notes */}
          {prescription.notes && (
            <div className="border border-gray-300 rounded-lg p-4 mb-6">
              <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">Notes / Instructions</h3>
              <p className="text-gray-700">{prescription.notes}</p>
            </div>
          )}

          {/* Footer */}
          <div className="border-t-2 border-gray-800 pt-4 mt-6">
            <div className="flex justify-between items-center text-sm text-gray-500">
              <div>
                <p>Status: <span className={`font-medium ${prescription.status === 'active' ? 'text-green-600' : 'text-amber-600'}`}>
                  {prescription.status?.toUpperCase()}
                </span></p>
              </div>
              <div className="text-right">
                <p>Generated by EUMedTranslate</p>
                <p className="text-xs">Cross-border prescription translation</p>
              </div>
            </div>
          </div>

          {/* Signature */}
          <div className="mt-8 pt-4">
            <div className="w-64">
              {prescription.doctor_signature ? (
                <div>
                  <img 
                    src={prescription.doctor_signature} 
                    alt="Doctor's signature" 
                    className="h-16 mb-1"
                  />
                  <div className="border-t border-gray-400 pt-1">
                    <p className="text-xs text-gray-500">Doctor's Signature</p>
                  </div>
                </div>
              ) : (
                <div className="border-t border-gray-400 pt-2">
                  <p className="text-xs text-gray-500">Doctor's Signature</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Payment Modal for patients without access */}
      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        prescriptionId={prescription.id}
        onPaymentSuccess={() => {
          setHasAccess(true);
          setShowPaymentModal(false);
        }}
      />
    </div>
  );
};

// Signature Modal for Doctors
const SignatureModal = ({ isOpen, onClose, onSave, currentSignature }) => {
  const sigCanvas = useRef(null);
  const [loading, setLoading] = useState(false);

  const clear = () => {
    sigCanvas.current?.clear();
  };

  const save = async () => {
    if (sigCanvas.current?.isEmpty()) {
      toast.error("Please draw your signature first");
      return;
    }

    setLoading(true);
    try {
      // Get the canvas and compress it
      const canvas = sigCanvas.current.getCanvas();
      
      // Create a smaller canvas for the signature (max 400px wide)
      const maxWidth = 400;
      const scale = canvas.width > maxWidth ? maxWidth / canvas.width : 1;
      
      const smallCanvas = document.createElement('canvas');
      smallCanvas.width = canvas.width * scale;
      smallCanvas.height = canvas.height * scale;
      const ctx = smallCanvas.getContext('2d');
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, smallCanvas.width, smallCanvas.height);
      ctx.drawImage(canvas, 0, 0, smallCanvas.width, smallCanvas.height);
      
      // Convert to data URL
      const signatureData = smallCanvas.toDataURL("image/png");
      
      console.log("Signature size:", Math.round(signatureData.length / 1024), "KB");
      
      const response = await api.post("/doctors/signature", { signature: signatureData });
      console.log("Save response:", response.data);
      toast.success("Signature saved successfully!");
      onSave(signatureData);
      onClose();
    } catch (err) {
      console.error("Signature save error:", err);
      const errorMsg = err.response?.data?.detail || err.message || "Failed to save signature";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Your Signature</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-sm text-gray-600 mb-4">
          Draw your signature below. This will be added to all prescriptions you create.
        </p>

        {/* Current signature preview */}
        {currentSignature && (
          <div className="mb-4 p-3 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-500 mb-2">Current signature:</p>
            <img src={currentSignature} alt="Current signature" className="h-16 mx-auto" />
          </div>
        )}

        {/* Signature canvas */}
        <div className="border-2 border-gray-300 rounded-xl mb-4 bg-white touch-none">
          <SignatureCanvas
            ref={sigCanvas}
            penColor="black"
            canvasProps={{
              className: "w-full h-40 rounded-xl touch-none",
              style: { width: "100%", height: "160px", touchAction: "none" }
            }}
            backgroundColor="white"
          />
        </div>

        <div className="flex gap-3">
          <button
            onClick={clear}
            className="flex-1 py-2 border border-gray-300 rounded-xl font-medium hover:bg-gray-50 transition"
          >
            Clear
          </button>
          <button
            onClick={save}
            disabled={loading}
            className="flex-1 py-2 bg-emerald-700 text-white rounded-xl font-medium hover:bg-emerald-800 transition disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading && <Loader2 className="w-5 h-5 animate-spin" />}
            Save Signature
          </button>
        </div>
      </div>
    </div>
  );
};

// Doctor Pending Approval Page
const DoctorPendingPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [uploading, setUploading] = useState(false);
  const [hasDocument, setHasDocument] = useState(false);

  const handleUploadDocument = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      await api.post('/doctors/license-document', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setHasDocument(true);
      toast.success("License document uploaded successfully!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md text-center">
        <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <Clock className="w-10 h-10 text-amber-600" />
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Account Pending Approval</h1>
        <p className="text-gray-600 mb-6">
          Your registration is being reviewed by our admin team. This usually takes 1-2 business days.
        </p>
        
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
          <h3 className="font-semibold text-gray-900 mb-4">Speed up your approval</h3>
          <p className="text-sm text-gray-600 mb-4">
            Upload your medical license document to help verify your credentials faster.
          </p>
          
          {hasDocument ? (
            <div className="flex items-center justify-center gap-2 text-emerald-600">
              <CheckCircle className="w-5 h-5" />
              <span>Document uploaded</span>
            </div>
          ) : (
            <label className="block">
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={handleUploadDocument}
                className="hidden"
                disabled={uploading}
              />
              <div className="px-4 py-3 bg-emerald-700 text-white rounded-xl font-medium hover:bg-emerald-800 transition cursor-pointer flex items-center justify-center gap-2">
                {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
                Upload License Document
              </div>
            </label>
          )}
        </div>
        
        <p className="text-sm text-gray-500 mb-4">
          You'll receive an email notification once your account is approved.
        </p>
        
        <button
          onClick={() => { logout(); navigate('/login'); }}
          className="text-gray-600 hover:text-gray-900"
        >
          Sign out
        </button>
      </div>
    </div>
  );
};

// Doctor Rejected Page
const DoctorRejectedPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md text-center">
        <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <XCircle className="w-10 h-10 text-red-600" />
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Registration Rejected</h1>
        <p className="text-gray-600 mb-4">
          Unfortunately, your doctor registration could not be verified.
        </p>
        
        {user?.rejection_reason && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 text-left">
            <p className="text-sm font-medium text-red-800 mb-1">Reason:</p>
            <p className="text-sm text-red-700">{user.rejection_reason}</p>
          </div>
        )}
        
        <p className="text-sm text-gray-500 mb-6">
          If you believe this is an error, please contact support at support@eumedtranslate.com
        </p>
        
        <button
          onClick={() => { logout(); navigate('/login'); }}
          className="text-gray-600 hover:text-gray-900"
        >
          Sign out
        </button>
      </div>
    </div>
  );
};

// Admin Dashboard
const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [patients, setPatients] = useState([]);
  const [prescriptions, setPrescriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [filter, setFilter] = useState("all");
  const [activeTab, setActiveTab] = useState("doctors"); // doctors, patients, prescriptions
  const [actionLoading, setActionLoading] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showResetUserPasswordModal, setShowResetUserPasswordModal] = useState(false);

  useEffect(() => {
    fetchData();
  }, [filter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, doctorsRes, patientsRes, prescriptionsRes] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/admin/doctors'),
        api.get('/admin/patients'),
        api.get('/admin/prescriptions')
      ]);
      setStats(statsRes.data);
      setDoctors(doctorsRes.data);
      setPatients(patientsRes.data || []);
      setPrescriptions(prescriptionsRes.data || []);
    } catch (err) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (doctorId, action, reason = null) => {
    setActionLoading(true);
    try {
      await api.post(`/admin/doctors/${doctorId}/verify`, {
        action,
        rejection_reason: reason
      });
      toast.success(`Doctor ${action === 'approve' ? 'approved' : 'rejected'} successfully! Email notification sent.`);
      setSelectedDoctor(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
    } finally {
      setActionLoading(false);
    }
  };

  const filteredDoctors = doctors.filter(d => {
    if (filter === "all") return true;
    return d.status === filter;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-emerald-700 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <nav className="bg-white shadow-sm border-b">
        <div className="max-w-6xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-700 rounded-xl flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-gray-900">Admin Dashboard</h1>
              <p className="text-xs text-gray-500">{user?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowResetUserPasswordModal(true)}
              className="p-2 hover:bg-gray-100 rounded-lg text-red-600"
              title="Reset User Password"
            >
              <Key className="w-5 h-5" />
            </button>
            <button
              onClick={() => setShowPasswordModal(true)}
              className="p-2 hover:bg-gray-100 rounded-lg text-gray-600"
              title="Change My Password"
            >
              <CreditCard className="w-5 h-5" />
            </button>
            <button
              onClick={async () => {
                try {
                  const res = await api.post('/admin/send-reminders');
                  toast.success(`Reminders sent: ${res.data.sent}, Failed: ${res.data.failed}`);
                } catch (err) {
                  toast.error("Failed to send reminders");
                }
              }}
              className="p-2 hover:bg-gray-100 rounded-lg text-gray-600"
              title="Send Prescription Reminders"
            >
              <Mail className="w-5 h-5" />
            </button>
            <button
              onClick={() => { logout(); navigate('/login'); }}
              className="p-2 hover:bg-gray-100 rounded-lg"
              title="Logout"
            >
              <LogOut className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Stats Cards - Clickable */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <button 
            onClick={() => { setActiveTab("doctors"); setFilter("pending"); }}
            className={`bg-white rounded-xl p-4 shadow-sm border text-left transition hover:shadow-md ${activeTab === "doctors" && filter === "pending" ? "ring-2 ring-amber-500" : ""}`}
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <span className="text-2xl font-bold text-gray-900">{stats?.doctors?.pending || 0}</span>
            </div>
            <p className="text-sm text-gray-600">Pending Doctors</p>
          </button>
          <button 
            onClick={() => { setActiveTab("doctors"); setFilter("approved"); }}
            className={`bg-white rounded-xl p-4 shadow-sm border text-left transition hover:shadow-md ${activeTab === "doctors" && filter === "approved" ? "ring-2 ring-emerald-500" : ""}`}
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
              </div>
              <span className="text-2xl font-bold text-gray-900">{stats?.doctors?.approved || 0}</span>
            </div>
            <p className="text-sm text-gray-600">Approved Doctors</p>
          </button>
          <button 
            onClick={() => setActiveTab("patients")}
            className={`bg-white rounded-xl p-4 shadow-sm border text-left transition hover:shadow-md ${activeTab === "patients" ? "ring-2 ring-blue-500" : ""}`}
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <span className="text-2xl font-bold text-gray-900">{stats?.patients || 0}</span>
            </div>
            <p className="text-sm text-gray-600">Patients</p>
          </button>
          <button 
            onClick={() => setActiveTab("prescriptions")}
            className={`bg-white rounded-xl p-4 shadow-sm border text-left transition hover:shadow-md ${activeTab === "prescriptions" ? "ring-2 ring-purple-500" : ""}`}
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-purple-600" />
              </div>
              <span className="text-2xl font-bold text-gray-900">{stats?.prescriptions || 0}</span>
            </div>
            <p className="text-sm text-gray-600">Prescriptions</p>
          </button>
        </div>

        {/* Doctors List */}
        {activeTab === "doctors" && (
        <div className="bg-white rounded-2xl shadow-sm border">
          <div className="p-4 border-b flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <h2 className="text-lg font-bold text-gray-900">Doctor Registrations</h2>
            <div className="flex flex-wrap gap-2">
              {["pending", "approved", "rejected", "all"].map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => setFilter(f)}
                  data-testid={`filter-${f}`}
                  className={`px-4 py-2 text-sm rounded-lg transition cursor-pointer active:scale-95 ${
                    filter === f
                      ? "bg-emerald-700 text-white"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {filteredDoctors.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No {filter === "all" ? "" : filter} doctors found
            </div>
          ) : (
            <div className="divide-y">
              {filteredDoctors.map((doctor) => (
                <div key={doctor.id} className="p-4 flex items-center justify-between hover:bg-gray-50">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                      <User className="w-6 h-6 text-gray-500" />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">{doctor.name}</h3>
                      <p className="text-sm text-gray-500">{doctor.email}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-gray-500">License: {doctor.license_number}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          doctor.status === "approved" ? "bg-emerald-100 text-emerald-700" :
                          doctor.status === "rejected" ? "bg-red-100 text-red-700" :
                          "bg-amber-100 text-amber-700"
                        }`}>
                          {doctor.status}
                        </span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedDoctor(doctor)}
                    className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center gap-1"
                  >
                    <Eye className="w-4 h-4" /> Review
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        )}

        {/* Patients List */}
        {activeTab === "patients" && (
        <div className="bg-white rounded-2xl shadow-sm border">
          <div className="p-4 border-b">
            <h2 className="text-lg font-bold text-gray-900">Registered Patients</h2>
          </div>
          {patients.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No patients found</div>
          ) : (
            <div className="divide-y">
              {patients.map((patient) => (
                <div key={patient.id} className="p-4 flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <User className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-900">{patient.name}</h3>
                    <p className="text-sm text-gray-500">{patient.email}</p>
                    <p className="text-xs text-gray-400">Country: {patient.country || 'N/A'}</p>
                  </div>
                  <button
                    onClick={() => setSelectedPatient(patient)}
                    className="px-3 py-2 text-sm bg-blue-100 hover:bg-blue-200 rounded-lg flex items-center gap-1 text-blue-700"
                  >
                    <Settings className="w-4 h-4" /> Edit
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        )}

        {/* Prescriptions List */}
        {activeTab === "prescriptions" && (
        <div className="bg-white rounded-2xl shadow-sm border">
          <div className="p-4 border-b">
            <h2 className="text-lg font-bold text-gray-900">All Prescriptions</h2>
          </div>
          {prescriptions.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No prescriptions found</div>
          ) : (
            <div className="divide-y">
              {prescriptions.map((rx) => (
                <div key={rx.id} className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                        <FileText className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900">#{rx.prescription_number || rx.id?.slice(0, 8)}</h3>
                        <p className="text-sm text-gray-500">Patient: {rx.patient_name || rx.patient_email}</p>
                        <p className="text-xs text-gray-400">Email: {rx.patient_email || 'N/A'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        rx.notification_sent ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                      }`}>
                        {rx.notification_sent ? "Email Sent" : "Not Sent"}
                      </span>
                      {!rx.notification_sent && rx.patient_email && (
                        <button
                          onClick={async () => {
                            try {
                              const res = await api.post(`/admin/prescriptions/${rx.id}/resend-notification`);
                              if (res.data.success) {
                                toast.success(res.data.message);
                                fetchData();
                              } else {
                                toast.error(res.data.message);
                              }
                            } catch (err) {
                              toast.error(err.response?.data?.detail || "Failed to resend");
                            }
                          }}
                          className="px-2 py-1 text-xs bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg flex items-center gap-1"
                        >
                          <Mail className="w-3 h-3" /> Resend
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="ml-13 text-sm text-gray-500">
                    <p>Doctor: {rx.doctor_name || 'N/A'}</p>
                    <p>Medications: {rx.medications?.length || 0} items</p>
                    <p className="text-xs text-gray-400">{rx.created_at ? new Date(rx.created_at).toLocaleDateString() : ''}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        )}
      </main>

      {/* Doctor Review Modal */}
      {selectedDoctor && (
        <DoctorReviewModal
          doctor={selectedDoctor}
          onClose={() => setSelectedDoctor(null)}
          onVerify={handleVerify}
          loading={actionLoading}
          onDoctorUpdate={(updatedDoctor) => {
            setDoctors(doctors.map(d => d.id === updatedDoctor.id ? updatedDoctor : d));
            setSelectedDoctor(updatedDoctor);
          }}
        />
      )}

      {/* Password Change Modal */}
      {showPasswordModal && (
        <ChangePasswordModal onClose={() => setShowPasswordModal(false)} />
      )}

      {/* Reset User Password Modal */}
      {showResetUserPasswordModal && (
        <ResetUserPasswordModal onClose={() => setShowResetUserPasswordModal(false)} />
      )}

      {/* Edit Patient Modal */}
      {selectedPatient && (
        <EditPatientModal
          patient={selectedPatient}
          onClose={() => setSelectedPatient(null)}
          onUpdate={(updatedPatient) => {
            setPatients(patients.map(p => p.id === updatedPatient.id ? updatedPatient : p));
            setSelectedPatient(null);
          }}
        />
      )}
    </div>
  );
};

// Change Password Modal
const ChangePasswordModal = ({ onClose }) => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error("New passwords don't match");
      return;
    }
    
    if (newPassword.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    
    setLoading(true);
    try {
      await api.post('/admin/change-password', {
        current_password: currentPassword,
        new_password: newPassword
      });
      toast.success("Password changed successfully!");
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md">
        <div className="p-6 border-b flex justify-between items-center">
          <h2 className="text-xl font-bold">Change Password</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Minimum 8 characters"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
              required
              minLength={8}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
              required
              minLength={8}
            />
          </div>
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-3 bg-emerald-700 text-white rounded-xl font-medium hover:bg-emerald-800 transition disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading && <Loader2 className="w-5 h-5 animate-spin" />}
              Change Password
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Admin Reset User Password Modal
const ResetUserPasswordModal = ({ onClose }) => {
  const [email, setEmail] = useState("");
  const [userType, setUserType] = useState("patient");
  const [newPassword, setNewPassword] = useState("Test123!");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    setLoading(true);
    try {
      const res = await api.post('/admin/reset-user-password', {
        email,
        user_type: userType,
        new_password: newPassword
      });
      toast.success(`Password reset for ${res.data.user_name || email}!`);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md">
        <div className="p-6 border-b flex justify-between items-center">
          <h2 className="text-xl font-bold">Reset User Password</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">User Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="user@example.com"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">User Type</label>
            <select
              value={userType}
              onChange={(e) => setUserType(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
            >
              <option value="patient">Patient</option>
              <option value="doctor">Doctor</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
            <input
              type="text"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Minimum 6 characters"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
              required
              minLength={6}
            />
            <p className="text-xs text-gray-500 mt-1">Default: Test123!</p>
          </div>
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-3 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 transition disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading && <Loader2 className="w-5 h-5 animate-spin" />}
              Reset Password
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Edit Patient Modal for Admin
const EditPatientModal = ({ patient, onClose, onUpdate }) => {
  const [formData, setFormData] = useState({
    name: patient.name || "",
    email: patient.email || "",
    country: patient.country || ""
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.put(`/admin/patients/${patient.id}`, formData);
      toast.success("Patient updated successfully!");
      onUpdate(res.data.patient);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update patient");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md">
        <div className="p-6 border-b flex justify-between items-center">
          <h2 className="text-xl font-bold">Edit Patient</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
            <input
              type="text"
              value={formData.country}
              onChange={(e) => setFormData({...formData, country: e.target.value})}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            />
          </div>
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading && <Loader2 className="w-5 h-5 animate-spin" />}
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Doctor Review Modal for Admin
const DoctorReviewModal = ({ doctor, onClose, onVerify, loading, onDoctorUpdate }) => {
  const [rejectionReason, setRejectionReason] = useState("");
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({
    license_number: doctor.license_number || "",
    name: doctor.name || "",
    phone: doctor.phone || "",
    clinic_name: doctor.clinic_name || "",
    clinic_address: doctor.clinic_address || ""
  });
  const [saving, setSaving] = useState(false);

  const handleSaveChanges = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/admin/doctors/${doctor.id}`, editData);
      toast.success("Doctor information updated!");
      setEditMode(false);
      if (onDoctorUpdate) onDoctorUpdate(res.data.doctor);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update doctor");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b flex justify-between items-center">
          <h2 className="text-xl font-bold">Review Doctor Registration</h2>
          <div className="flex items-center gap-2">
            {!editMode && (
              <button
                onClick={() => setEditMode(true)}
                className="px-3 py-1.5 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 flex items-center gap-1"
              >
                <Pen className="w-4 h-4" /> Edit Info
              </button>
            )}
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Doctor Info */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <div>
                <label className="text-xs text-gray-500">Full Name</label>
                {editMode ? (
                  <input
                    type="text"
                    value={editData.name}
                    onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  />
                ) : (
                  <p className="font-medium">{doctor.name}</p>
                )}
              </div>
              <div>
                <label className="text-xs text-gray-500">Email</label>
                <p className="font-medium">{doctor.email}</p>
              </div>
              <div>
                <label className="text-xs text-gray-500">Phone</label>
                {editMode ? (
                  <input
                    type="text"
                    value={editData.phone}
                    onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  />
                ) : (
                  <p className="font-medium">{doctor.phone || "Not provided"}</p>
                )}
              </div>
              <div>
                <label className="text-xs text-gray-500">Country</label>
                <p className="font-medium">{doctor.country}</p>
              </div>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-gray-500">License Number</label>
                {editMode ? (
                  <input
                    type="text"
                    value={editData.license_number}
                    onChange={(e) => setEditData({ ...editData, license_number: e.target.value })}
                    className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-blue-50"
                    placeholder="Enter license number"
                  />
                ) : (
                  <p className="font-medium text-emerald-700">{doctor.license_number}</p>
                )}
              </div>
              <div>
                <label className="text-xs text-gray-500">Clinic Name</label>
                {editMode ? (
                  <input
                    type="text"
                    value={editData.clinic_name}
                    onChange={(e) => setEditData({ ...editData, clinic_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  />
                ) : (
                  <p className="font-medium">{doctor.clinic_name}</p>
                )}
              </div>
              <div>
                <label className="text-xs text-gray-500">Clinic Address</label>
                {editMode ? (
                  <input
                    type="text"
                    value={editData.clinic_address}
                    onChange={(e) => setEditData({ ...editData, clinic_address: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  />
                ) : (
                  <p className="font-medium">{doctor.clinic_address}</p>
                )}
              </div>
              <div>
                <label className="text-xs text-gray-500">Specialty</label>
                <p className="font-medium">{doctor.specialty || "General"}</p>
              </div>
            </div>
          </div>

          {/* Edit Mode Save/Cancel */}
          {editMode && (
            <div className="flex gap-3 justify-end border-t pt-4">
              <button
                onClick={() => {
                  setEditMode(false);
                  setEditData({
                    license_number: doctor.license_number || "",
                    name: doctor.name || "",
                    phone: doctor.phone || "",
                    clinic_name: doctor.clinic_name || "",
                    clinic_address: doctor.clinic_address || ""
                  });
                }}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveChanges}
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 disabled:opacity-50"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save Changes
              </button>
            </div>
          )}

          {/* License Document */}
          <div>
            <label className="text-xs text-gray-500 mb-2 block">License Document</label>
            {doctor.license_document ? (
              <div className="border rounded-xl p-4">
                {doctor.license_document.startsWith('data:image') ? (
                  <img
                    src={doctor.license_document}
                    alt="License Document"
                    className="max-w-full h-auto rounded-lg"
                  />
                ) : (
                  <a
                    href={doctor.license_document}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-emerald-700 hover:underline"
                  >
                    <FileCheck className="w-5 h-5" />
                    View Document
                  </a>
                )}
              </div>
            ) : (
              <div className="border rounded-xl p-4 text-center text-gray-500">
                <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-amber-500" />
                No document uploaded
              </div>
            )}
          </div>

          {/* Status */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Current Status:</span>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
              doctor.status === "approved" ? "bg-emerald-100 text-emerald-700" :
              doctor.status === "rejected" ? "bg-red-100 text-red-700" :
              "bg-amber-100 text-amber-700"
            }`}>
              {doctor.status}
            </span>
          </div>

          {/* Rejection Form */}
          {showRejectForm && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <label className="block text-sm font-medium text-red-800 mb-2">
                Rejection Reason (required)
              </label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Please provide a reason for rejection..."
                className="w-full px-3 py-2 border border-red-300 rounded-lg focus:ring-2 focus:ring-red-500 outline-none"
                rows={3}
              />
            </div>
          )}
        </div>

        {/* Actions */}
        {doctor.status === "pending" && (
          <div className="p-6 border-t flex gap-3 justify-end">
            {!showRejectForm ? (
              <>
                <button
                  onClick={() => setShowRejectForm(true)}
                  className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 flex items-center gap-2"
                >
                  <XCircle className="w-4 h-4" /> Reject
                </button>
                <button
                  onClick={() => onVerify(doctor.id, "approve")}
                  disabled={loading}
                  className="px-4 py-2 bg-emerald-700 text-white rounded-lg hover:bg-emerald-800 flex items-center gap-2 disabled:opacity-50"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                  Approve
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => setShowRejectForm(false)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={() => onVerify(doctor.id, "reject", rejectionReason)}
                  disabled={loading || !rejectionReason.trim()}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2 disabled:opacity-50"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
                  Confirm Rejection
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Doctor Profile Modal
const DoctorProfileModal = ({ isOpen, onClose, user, onProfileUpdate }) => {
  const [formData, setFormData] = useState({
    name: user?.name || "",
    email: user?.email || "",
    phone: user?.phone || "",
    clinic_name: user?.clinic_name || "",
    clinic_address: user?.clinic_address || "",
    specialty: user?.specialty || "",
    country: user?.country || ""
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && user) {
      setFormData({
        name: user.name || "",
        email: user.email || "",
        phone: user.phone || "",
        clinic_name: user.clinic_name || "",
        clinic_address: user.clinic_address || "",
        specialty: user.specialty || "",
        country: user.country || ""
      });
    }
  }, [isOpen, user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.put("/doctors/profile", formData);
      toast.success("Profile updated successfully!");
      onProfileUpdate(res.data.user);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-900">Edit Profile</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <User className="w-4 h-4 inline mr-1" />
              Full Name
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
              required
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Mail className="w-4 h-4 inline mr-1" />
              Email Address
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
              required
            />
          </div>

          {/* Phone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Phone className="w-4 h-4 inline mr-1" />
              Phone Number
            </label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
              required
            />
          </div>

          {/* Clinic Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Building2 className="w-4 h-4 inline mr-1" />
              Clinic / Hospital Name
            </label>
            <input
              type="text"
              value={formData.clinic_name}
              onChange={(e) => setFormData({ ...formData, clinic_name: e.target.value })}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
              required
            />
          </div>

          {/* Clinic Address */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <MapPin className="w-4 h-4 inline mr-1" />
              Clinic Address
            </label>
            <input
              type="text"
              value={formData.clinic_address}
              onChange={(e) => setFormData({ ...formData, clinic_address: e.target.value })}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
              required
            />
          </div>

          {/* Specialty */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Specialty (Optional)
            </label>
            <input
              type="text"
              value={formData.specialty}
              onChange={(e) => setFormData({ ...formData, specialty: e.target.value })}
              placeholder="e.g., General Practice, Cardiology"
              className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
            />
          </div>

          {/* Country */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <MapPin className="w-4 h-4 inline mr-1" />
              Country
            </label>
            <select
              value={formData.country}
              onChange={(e) => setFormData({ ...formData, country: e.target.value })}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
              required
            >
              <option value="">Select Country</option>
              {EU_COUNTRIES.map((c) => (
                <option key={c.code} value={c.code}>{c.name}</option>
              ))}
            </select>
          </div>

          {/* License Number - Read only */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <CreditCard className="w-4 h-4 inline mr-1" />
              License Number
            </label>
            <input
              type="text"
              value={user?.license_number || ""}
              disabled
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl bg-gray-50 text-gray-500"
            />
            <p className="text-xs text-gray-400 mt-1">License number cannot be changed. Contact support if needed.</p>
          </div>

          {/* Submit Button */}
          <div className="pt-4">
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-emerald-700 text-white rounded-xl font-medium hover:bg-emerald-800 transition disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Doctor Dashboard
const DoctorDashboard = () => {
  const { user, logout, login } = useAuth();
  const navigate = useNavigate();
  const [prescriptions, setPrescriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showScanModal, setShowScanModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showNewRxModal, setShowNewRxModal] = useState(false);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [selectedPrescription, setSelectedPrescription] = useState(null);
  const [editingPrescription, setEditingPrescription] = useState(null);
  const [renewingPrescription, setRenewingPrescription] = useState(null);
  const [signature, setSignature] = useState(user?.signature || null);
  const [currentUser, setCurrentUser] = useState(user);

  useEffect(() => {
    fetchPrescriptions();
    fetchSignature();
  }, []);

  const fetchPrescriptions = async () => {
    try {
      const res = await api.get("/prescriptions");
      setPrescriptions(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSignature = async () => {
    try {
      const res = await api.get("/doctors/signature");
      if (res.data.signature) {
        setSignature(res.data.signature);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleSignatureSave = (newSignature) => {
    setSignature(newSignature);
    // Update user in localStorage
    const updatedUser = { ...currentUser, signature: newSignature };
    setCurrentUser(updatedUser);
    localStorage.setItem("user", JSON.stringify(updatedUser));
  };

  const handleProfileUpdate = (updatedUser) => {
    setCurrentUser(updatedUser);
    localStorage.setItem("user", JSON.stringify(updatedUser));
    // Also update auth context
    login(localStorage.getItem("token"), updatedUser);
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const handleScanComplete = (newRx) => {
    setPrescriptions([newRx, ...prescriptions]);
    setSelectedPrescription(newRx);
  };

  const handleUploadComplete = (newRx) => {
    setPrescriptions([newRx, ...prescriptions]);
    setSelectedPrescription(newRx);
  };

  const handleNewRxComplete = (newRx) => {
    setPrescriptions([newRx, ...prescriptions]);
    setSelectedPrescription(newRx);
  };

  const handleEditComplete = (updatedRx) => {
    setPrescriptions(prescriptions.map(rx => rx.id === updatedRx.id ? updatedRx : rx));
    setEditingPrescription(null);
    toast.success("Prescription updated successfully!");
  };

  const handleDeletePrescription = async (rxId) => {
    if (!window.confirm("Are you sure you want to delete this prescription? This cannot be undone.")) {
      return;
    }
    try {
      await api.delete(`/prescriptions/${rxId}`);
      setPrescriptions(prescriptions.filter(rx => rx.id !== rxId));
      toast.success("Prescription deleted");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete prescription");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-4 md:px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 bg-emerald-700 rounded-xl flex items-center justify-center">
              <Pill className="w-6 h-6 text-white" />
            </div>
            <span className="text-lg md:text-xl font-bold text-emerald-900">EUMedTranslate</span>
          </div>
          <div className="flex items-center gap-2 md:gap-4">
            <span className="text-gray-600 text-sm md:text-base hidden sm:block">Dr. {currentUser?.name}</span>
            <button
              data-testid="profile-btn"
              onClick={() => setShowProfileModal(true)}
              className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg flex items-center gap-2"
              title="Edit Profile"
            >
              <Settings className="w-5 h-5" />
            </button>
            <button
              data-testid="signature-btn"
              onClick={() => setShowSignatureModal(true)}
              className={`p-2 rounded-lg flex items-center gap-2 ${signature ? 'text-emerald-700 hover:bg-emerald-50' : 'text-amber-600 hover:bg-amber-50'}`}
              title={signature ? "Update signature" : "Add signature"}
            >
              <Pen className="w-5 h-5" />
              <span className="hidden sm:inline text-sm">{signature ? "Signed" : "Sign"}</span>
            </button>
            <button
              data-testid="logout-btn"
              onClick={handleLogout}
              className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg flex items-center gap-2"
            >
              <LogOut className="w-5 h-5" />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 md:px-6 py-6 md:py-8">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-6 md:mb-8">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-gray-900">Doctor Dashboard</h1>
            <p className="text-gray-600 text-sm md:text-base">Create and manage prescriptions</p>
          </div>
          <div className="flex gap-2 md:gap-3">
            <button
              data-testid="scan-btn"
              onClick={() => setShowScanModal(true)}
              className="flex-1 md:flex-none px-4 md:px-5 py-2.5 bg-emerald-700 text-white rounded-xl font-medium hover:bg-emerald-800 transition flex items-center justify-center gap-2"
            >
              <ScanLine className="w-5 h-5" />
              <span>Scan</span>
            </button>
            <button
              data-testid="upload-btn"
              onClick={() => setShowUploadModal(true)}
              className="flex-1 md:flex-none px-4 md:px-5 py-2.5 border border-emerald-700 text-emerald-700 rounded-xl font-medium hover:bg-emerald-50 transition flex items-center justify-center gap-2"
            >
              <Upload className="w-5 h-5" />
              <span>Upload</span>
            </button>
            <button
              data-testid="new-prescription-btn"
              onClick={() => setShowNewRxModal(true)}
              className="flex-1 md:flex-none px-4 md:px-5 py-2.5 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition flex items-center justify-center gap-2"
            >
              <Plus className="w-5 h-5" />
              <span className="hidden sm:inline">Manual</span>
            </button>
          </div>
        </div>

        {/* Signature reminder */}
        {!signature && (
          <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Pen className="w-5 h-5 text-amber-600" />
              <div>
                <p className="font-medium text-amber-800">Add your signature</p>
                <p className="text-sm text-amber-600">Your prescriptions will include your digital signature</p>
              </div>
            </div>
            <button
              onClick={() => setShowSignatureModal(true)}
              className="px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700"
            >
              Add Signature
            </button>
          </div>
        )}

        {/* Prescriptions List */}
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-700 mx-auto" />
            </div>
          ) : prescriptions.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-600">No prescriptions yet</h3>
              <p className="text-gray-500">Create your first prescription to get started</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Rx Number</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Patient</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Action</th>
                </tr>
              </thead>
              <tbody>
                {prescriptions.map((rx) => (
                  <tr key={rx.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium">{rx.prescription_number}</td>
                    <td className="px-6 py-4">{rx.patient_name}</td>
                    <td className="px-6 py-4 text-gray-600">
                      {new Date(rx.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        rx.status === 'active' 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-amber-100 text-amber-700'
                      }`}>
                        {rx.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          data-testid={`view-rx-${rx.id}`}
                          onClick={() => setSelectedPrescription(rx)}
                          className="text-emerald-700 font-medium text-sm hover:underline"
                        >
                          View
                        </button>
                        <span className="text-gray-300">|</span>
                        <button
                          data-testid={`edit-rx-${rx.id}`}
                          onClick={() => setEditingPrescription(rx)}
                          className="text-blue-600 font-medium text-sm hover:underline"
                        >
                          Edit
                        </button>
                        <span className="text-gray-300">|</span>
                        <button
                          data-testid={`renew-rx-${rx.id}`}
                          onClick={() => setRenewingPrescription(rx)}
                          className="text-emerald-600 font-medium text-sm hover:underline"
                        >
                          Renew
                        </button>
                        <span className="text-gray-300">|</span>
                        <button
                          data-testid={`delete-rx-${rx.id}`}
                          onClick={() => handleDeletePrescription(rx.id)}
                          className="text-red-600 font-medium text-sm hover:underline"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>

      {/* Modals */}
      <ScanModal
        isOpen={showScanModal}
        onClose={() => setShowScanModal(false)}
        onScanComplete={handleScanComplete}
      />
      <UploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUploadComplete={handleUploadComplete}
      />
      <NewPrescriptionModal
        isOpen={showNewRxModal}
        onClose={() => setShowNewRxModal(false)}
        onComplete={handleNewRxComplete}
      />
      <SignatureModal
        isOpen={showSignatureModal}
        onClose={() => setShowSignatureModal(false)}
        onSave={handleSignatureSave}
        currentSignature={signature}
      />
      <DoctorProfileModal
        isOpen={showProfileModal}
        onClose={() => setShowProfileModal(false)}
        user={currentUser}
        onProfileUpdate={handleProfileUpdate}
      />
      <EditPrescriptionModal
        prescription={editingPrescription}
        isOpen={!!editingPrescription}
        onClose={() => setEditingPrescription(null)}
        onComplete={handleEditComplete}
      />
      <RenewPrescriptionModal
        prescription={renewingPrescription}
        isOpen={!!renewingPrescription}
        onClose={() => setRenewingPrescription(null)}
        onComplete={(newRx) => {
          setPrescriptions([newRx, ...prescriptions]);
          setRenewingPrescription(null);
        }}
      />
      {selectedPrescription && (
        <PrescriptionReceipt
          prescription={selectedPrescription}
          onClose={() => setSelectedPrescription(null)}
          onEdit={(rx) => {
            setSelectedPrescription(null);
            setEditingPrescription(rx);
          }}
          onRenew={(rx) => {
            setSelectedPrescription(null);
            setRenewingPrescription(rx);
          }}
        />
      )}
    </div>
  );
};


// Payment Success Page
const PaymentSuccessPage = () => {
  const navigate = useNavigate();
  const [verifying, setVerifying] = useState(true);
  const [success, setSuccess] = useState(false);
  const [accessType, setAccessType] = useState(null);

  useEffect(() => {
    const verifyPayment = async () => {
      const params = new URLSearchParams(window.location.search);
      const sessionId = params.get('session_id');
      
      if (!sessionId) {
        setVerifying(false);
        return;
      }
      
      try {
        const res = await api.get(`/payments/verify/${sessionId}`);
        if (res.data.success) {
          setSuccess(true);
          setAccessType(res.data.access_type);
        }
      } catch (err) {
        console.error("Payment verification failed:", err);
      } finally {
        setVerifying(false);
      }
    };
    
    verifyPayment();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
        {verifying ? (
          <>
            <Loader2 className="w-16 h-16 text-emerald-600 animate-spin mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Verifying Payment...</h2>
            <p className="text-gray-600">Please wait while we confirm your payment.</p>
          </>
        ) : success ? (
          <>
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment Successful!</h2>
            <p className="text-gray-600 mb-6">
              {accessType === 'subscription' 
                ? "Your monthly subscription is now active. Enjoy unlimited access to all your prescriptions!"
                : "You now have access to download and print your prescription."}
            </p>
            <button
              onClick={() => navigate('/patient')}
              className="w-full py-3 bg-emerald-700 text-white rounded-xl font-semibold hover:bg-emerald-800 transition"
            >
              Go to My Prescriptions
            </button>
          </>
        ) : (
          <>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-10 h-10 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment Not Verified</h2>
            <p className="text-gray-600 mb-6">
              We couldn't verify your payment. Please try again or contact support.
            </p>
            <button
              onClick={() => navigate('/patient')}
              className="w-full py-3 bg-gray-600 text-white rounded-xl font-semibold hover:bg-gray-700 transition"
            >
              Return to Dashboard
            </button>
          </>
        )}
      </div>
    </div>
  );
};


// Patient Dashboard
const PatientDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [prescriptions, setPrescriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPrescription, setSelectedPrescription] = useState(null);
  const [showTranslateModal, setShowTranslateModal] = useState(false);

  useEffect(() => {
    fetchPrescriptions();
  }, []);

  const fetchPrescriptions = async () => {
    try {
      const res = await api.get("/prescriptions/patient");
      setPrescriptions(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const handleTranslate = (prescription) => {
    setSelectedPrescription(prescription);
    setShowTranslateModal(true);
  };

  const handleMarkUsed = async (prescriptionId) => {
    if (!window.confirm("Are you sure you want to mark this prescription as used? This action cannot be undone.")) {
      return;
    }
    try {
      await api.post(`/prescriptions/${prescriptionId}/mark-used`);
      toast.success("Prescription marked as used");
      fetchPrescriptions(); // Refresh the list
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to mark as used");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-4 md:px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 bg-emerald-700 rounded-xl flex items-center justify-center">
              <Pill className="w-6 h-6 text-white" />
            </div>
            <span className="text-lg md:text-xl font-bold text-emerald-900">EUMedTranslate</span>
          </div>
          <div className="flex items-center gap-2 md:gap-4">
            <span className="text-gray-600 text-sm md:text-base hidden sm:block">{user?.name}</span>
            <button
              data-testid="patient-logout-btn"
              onClick={handleLogout}
              className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg flex items-center gap-2"
            >
              <LogOut className="w-5 h-5" />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 md:px-6 py-6 md:py-8">
        <div className="mb-6">
          <h1 className="text-xl md:text-2xl font-bold text-gray-900">My Prescriptions</h1>
          <p className="text-gray-600 text-sm md:text-base">View and translate your prescriptions</p>
        </div>

        {loading ? (
          <div className="p-12 text-center">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-700 mx-auto" />
          </div>
        ) : prescriptions.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
            <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-600">No prescriptions yet</h3>
            <p className="text-gray-500">Your doctor will send prescriptions here</p>
          </div>
        ) : (
          <div className="space-y-4">
            {prescriptions.map((rx) => (
              <div key={rx.id} className={`bg-white rounded-2xl border ${rx.used ? 'border-red-200 bg-red-50' : 'border-gray-200'} p-4 md:p-6`}>
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <p className="font-semibold text-lg">{rx.prescription_number}</p>
                    <p className="text-sm text-gray-500">From: Dr. {rx.doctor_name}</p>
                    <p className="text-sm text-gray-500">{rx.clinic_name}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      rx.used 
                        ? 'bg-red-100 text-red-700'
                        : rx.status === 'active' 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-amber-100 text-amber-700'
                    }`}>
                      {rx.used ? 'USED' : rx.status}
                    </span>
                    {rx.used && (
                      <span className="text-xs text-red-500">Used: {new Date(rx.used_at).toLocaleDateString()}</span>
                    )}
                  </div>
                </div>

                {/* Valid once warning */}
                {!rx.used && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                    <p className="text-sm font-medium text-amber-800">⚠️ Valid for one-time use only</p>
                    <p className="text-xs text-amber-600">Pharmacist must mark as used after dispensing</p>
                  </div>
                )}

                <div className="border-t border-gray-100 pt-4 mb-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">Medications:</p>
                  {rx.medications?.map((med, idx) => (
                    <div key={idx} className="text-sm text-gray-600 mb-1">
                      • {med.name} {med.dosage && `- ${med.dosage}`} {med.frequency && `(${med.frequency})`}
                    </div>
                  ))}
                </div>

                <div className="flex gap-2 flex-wrap">
                  <button
                    data-testid={`view-rx-${rx.id}`}
                    onClick={() => setSelectedPrescription(rx)}
                    className="flex-1 py-2 border border-gray-300 rounded-xl text-sm font-medium hover:bg-gray-50 transition"
                  >
                    View Details
                  </button>
                  {!rx.used && (
                    <>
                      <button
                        data-testid={`translate-rx-${rx.id}`}
                        onClick={() => handleTranslate(rx)}
                        className="flex-1 py-2 bg-emerald-700 text-white rounded-xl text-sm font-medium hover:bg-emerald-800 transition flex items-center justify-center gap-2"
                      >
                        <MapPin className="w-4 h-4" />
                        Translate
                      </button>
                      <button
                        data-testid={`mark-used-${rx.id}`}
                        onClick={() => handleMarkUsed(rx.id)}
                        className="py-2 px-4 bg-red-600 text-white rounded-xl text-sm font-medium hover:bg-red-700 transition"
                      >
                        Mark as Used
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* View Prescription Receipt */}
      {selectedPrescription && !showTranslateModal && (
        <PrescriptionReceipt
          prescription={selectedPrescription}
          onClose={() => setSelectedPrescription(null)}
        />
      )}

      {/* Translate Modal */}
      {showTranslateModal && selectedPrescription && (
        <TranslateModal
          prescription={selectedPrescription}
          onClose={() => { setShowTranslateModal(false); setSelectedPrescription(null); }}
        />
      )}
    </div>
  );
};

// Translate Modal for Patients - Shows printable translated prescription
const TranslateModal = ({ prescription, onClose }) => {
  const { user } = useAuth();
  const [destinationCountry, setDestinationCountry] = useState("");
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [translatedData, setTranslatedData] = useState(null);
  const [medicationNotes, setMedicationNotes] = useState([]);
  const [hasAccess, setHasAccess] = useState(user?.user_type === 'doctor');
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  // Check payment access for patients
  useEffect(() => {
    const checkAccess = async () => {
      if (user?.user_type === 'patient') {
        try {
          const res = await api.get(`/payments/access-check/${prescription.id}`);
          setHasAccess(res.data.has_access);
        } catch (err) {
          setHasAccess(false);
        }
      }
    };
    checkAccess();
  }, [prescription.id, user]);

  // Check access before performing action
  const checkAccessAndPerform = (action) => {
    if (user?.user_type === 'doctor' || hasAccess) {
      action();
    } else {
      setShowPaymentModal(true);
    }
  };

  const getCountryName = (code) => {
    const country = EU_COUNTRIES.find(c => c.code === code);
    return country ? country.name : code;
  };

  const handleTranslate = async () => {
    if (!destinationCountry) {
      toast.error("Please select a destination country");
      return;
    }
    
    setLoading(true);
    try {
      const res = await api.post("/prescriptions/translate", {
        prescription_id: prescription.id,
        destination_country: destinationCountry
      });
      setTranslatedData(res.data.translated);
      setMedicationNotes(res.data.medication_notes || []);
      toast.success(`Prescription translated to ${res.data.destination_language}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Translation failed");
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    checkAccessAndPerform(() => {
      const receiptContent = document.getElementById('prescription-receipt');
      if (!receiptContent) {
        toast.error("Could not find prescription content to print");
        return;
      }
      
      const printWindow = window.open('', '_blank', 'width=800,height=600');
      if (!printWindow) {
        window.print();
        return;
      }
      
      printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Translated Prescription - ${prescription.prescription_number}</title>
        <style>
          * { box-sizing: border-box; margin: 0; padding: 0; }
          body { font-family: -apple-system, BlinkMacSystemFont, sans-serif; padding: 20px; background: white; }
          .border-2 { border: 2px solid #1f2937; }
          .border { border: 1px solid #d1d5db; }
          .rounded-xl { border-radius: 12px; }
          .rounded-lg { border-radius: 8px; }
          .p-6 { padding: 24px; }
          .p-4 { padding: 16px; }
          .p-3 { padding: 12px; }
          .mb-6 { margin-bottom: 24px; }
          .mb-4 { margin-bottom: 16px; }
          .mb-3 { margin-bottom: 12px; }
          .mb-2 { margin-bottom: 8px; }
          .mt-1 { margin-top: 4px; }
          .mt-8 { margin-top: 32px; }
          .pt-4 { padding-top: 16px; }
          .flex { display: flex; }
          .justify-between { justify-content: space-between; }
          .items-start { align-items: flex-start; }
          .items-center { align-items: center; }
          .gap-4 { gap: 16px; }
          .gap-2 { gap: 8px; }
          .grid { display: grid; }
          .grid-cols-2 { grid-template-columns: repeat(2, 1fr); gap: 16px; }
          .space-y-2 > * + * { margin-top: 8px; }
          .text-center { text-align: center; }
          .text-right { text-align: right; }
          .text-xl { font-size: 20px; }
          .text-lg { font-size: 18px; }
          .text-sm { font-size: 14px; }
          .text-xs { font-size: 12px; }
          .font-bold { font-weight: 700; }
          .font-semibold { font-weight: 600; }
          .font-medium { font-weight: 500; }
          .text-gray-900 { color: #111827; }
          .text-gray-700 { color: #374151; }
          .text-gray-600 { color: #4b5563; }
          .text-gray-500 { color: #6b7280; }
          .text-emerald-700 { color: #047857; }
          .text-white { color: white; }
          .bg-white { background: white; }
          .bg-gray-50 { background: #f9fafb; }
          .bg-gray-100 { background: #f3f4f6; }
          .bg-emerald-700 { background: #047857; color: white; }
          .bg-amber-50 { background: #fffbeb; }
          .bg-amber-100 { background: #fef3c7; }
          .text-amber-800 { color: #92400e; }
          .text-amber-600 { color: #d97706; }
          .border-amber-200 { border-color: #fde68a; }
          .uppercase { text-transform: uppercase; }
          .border-t { border-top: 1px solid #d1d5db; }
          .border-t-2 { border-top: 2px solid #1f2937; }
          .border-b { border-bottom: 1px solid #d1d5db; }
          img { max-width: 200px; height: auto; }
          table { width: 100%; border-collapse: collapse; }
          th, td { padding: 8px 12px; text-align: left; border: 1px solid #d1d5db; }
          th { background: #047857; color: white; }
          @media print { body { padding: 10px; } }
        </style>
      </head>
      <body>
        ${receiptContent.innerHTML}
      </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    
    setTimeout(() => {
      printWindow.print();
      setTimeout(() => printWindow.close(), 500);
    }, 250);
    });
  };

  const handleDownloadPDF = async () => {
    checkAccessAndPerform(async () => {
      setDownloading(true);
      try {
        let response;
        let filename;
        
        if (translatedData && destinationCountry) {
          // Download translated PDF
          response = await api.post(
            `/prescriptions/${prescription.id}/pdf/translated`, 
            { destination_country: destinationCountry },
            { responseType: 'blob' }
          );
          filename = `prescription_${prescription.prescription_number}_translated_${destinationCountry}.pdf`;
        } else {
          // Download original PDF
          response = await api.get(`/prescriptions/${prescription.id}/pdf`, {
            responseType: 'blob'
          });
          filename = `prescription_${prescription.prescription_number}.pdf`;
        }
        
        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        toast.success("Translated PDF downloaded!");
      } catch (err) {
        console.error("PDF download error:", err);
        toast.error(err.response?.data?.detail || "Failed to download PDF");
      } finally {
        setDownloading(false);
      }
    });
  };

  // Use translated data if available, otherwise original
  const displayData = translatedData || prescription;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 print:bg-white print:p-0">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto print:max-h-none print:overflow-visible print:rounded-none printable-modal">
        {!translatedData ? (
          // Step 1: Select destination country
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold">Translate Prescription</h2>
              <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-4 p-4 bg-gray-50 rounded-xl">
              <p className="text-sm text-gray-500">Prescription</p>
              <p className="font-semibold">{prescription.prescription_number}</p>
              <p className="text-sm text-gray-600">Origin: {getCountryName(prescription.origin_country)}</p>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Where do you need this prescription?
              </label>
              <select
                data-testid="translate-country-select"
                value={destinationCountry}
                onChange={(e) => setDestinationCountry(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
              >
                <option value="">Select destination country</option>
                {EU_COUNTRIES.filter(c => c.code !== prescription.origin_country).map((c) => (
                  <option key={c.code} value={c.code}>{c.name}</option>
                ))}
              </select>
            </div>

            <button
              data-testid="translate-btn"
              onClick={handleTranslate}
              disabled={!destinationCountry || loading}
              className="w-full py-3 bg-emerald-700 text-white rounded-xl font-medium hover:bg-emerald-800 transition disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading && <Loader2 className="w-5 h-5 animate-spin" />}
              {loading ? "Translating..." : "Translate Prescription"}
            </button>
          </div>
        ) : (
          // Step 2: Show translated receipt
          <>
            <div className="p-4 border-b flex justify-between items-center no-print">
              <h2 className="text-xl font-bold">Translated Prescription</h2>
              <div className="flex gap-2">
                <button
                  data-testid="download-translated-pdf-btn"
                  onClick={handleDownloadPDF}
                  disabled={downloading}
                  className="px-4 py-2 bg-amber-600 text-white rounded-lg flex items-center gap-2 hover:bg-amber-700 disabled:opacity-50"
                >
                  {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
                  PDF
                </button>
                <button
                  data-testid="print-translated-btn"
                  onClick={handlePrint}
                  className="px-4 py-2 bg-emerald-700 text-white rounded-lg flex items-center gap-2"
                >
                  <Download className="w-4 h-4" /> Print
                </button>
                <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Printable Translated Receipt */}
            <div className="p-6 print:p-4" id="prescription-receipt">
              {/* Header with Translation Banner */}
              <div className="bg-emerald-700 text-white p-4 rounded-xl mb-6 print:bg-emerald-700">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm opacity-90">EU Cross-Border Prescription</p>
                    <p className="text-lg font-bold">TRANSLATED FOR {getCountryName(displayData.destination_country).toUpperCase()}</p>
                    <p className="text-sm opacity-90">Language: {displayData.destination_language}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm opacity-90">Original from</p>
                    <p className="font-semibold">{getCountryName(displayData.origin_country)}</p>
                  </div>
                </div>
              </div>

              {/* Prescription Info */}
              <div className="border-2 border-gray-800 p-6 mb-6">
                <div className="flex justify-between items-start">
                  {/* Clinic Info */}
                  <div className="flex gap-4">
                    <div className="w-14 h-14 bg-emerald-700 rounded-xl flex items-center justify-center">
                      <Pill className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <h1 className="text-lg font-bold text-gray-900">{displayData.clinic_name || "Medical Clinic"}</h1>
                      <p className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                        <MapPin className="w-4 h-4" />
                        {displayData.clinic_address || "Address not provided"}
                      </p>
                      <p className="text-sm text-gray-600 flex items-center gap-1">
                        <Phone className="w-4 h-4" />
                        {displayData.doctor_phone || "Phone not provided"}
                      </p>
                      <p className="text-sm text-gray-600 flex items-center gap-1">
                        <Mail className="w-4 h-4" />
                        {displayData.doctor_email || "Email not provided"}
                      </p>
                    </div>
                  </div>

                  {/* Receipt Number */}
                  <div className="text-right">
                    <div className="bg-gray-100 px-4 py-2 rounded mb-2">
                      <p className="text-xs text-gray-500">PRESCRIPTION NO.</p>
                      <p className="font-bold">{displayData.prescription_number}</p>
                    </div>
                    <p className="text-sm text-gray-600">Date: {new Date(displayData.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
              </div>

              {/* Doctor & Patient Info */}
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <div className="border border-gray-300 rounded-lg p-4">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase mb-3 border-b pb-2">Prescribing Doctor</h3>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-gray-400" />
                      <span className="font-medium">{displayData.doctor_name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CreditCard className="w-4 h-4 text-gray-400" />
                      <span className="text-sm">License: {displayData.doctor_license || "N/A"}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-gray-400" />
                      <span className="text-sm">{displayData.doctor_email}</span>
                    </div>
                  </div>
                </div>

                <div className="border border-gray-300 rounded-lg p-4">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase mb-3 border-b pb-2">Patient Information</h3>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-gray-400" />
                      <span className="font-medium">{displayData.patient_name}</span>
                    </div>
                    {displayData.patient_dob && (
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        <span className="text-sm">DOB: {displayData.patient_dob}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Medications - With translated names */}
              <div className="mb-6">
                <h3 className="text-xs font-semibold text-gray-500 uppercase mb-3">Medications</h3>
                {displayData.medications?.map((med, index) => (
                  <div key={index} className="border border-gray-300 rounded-lg p-3 mb-2 bg-gray-50">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <span className="font-bold text-gray-900">{med.translated_name || med.name}</span>
                        {med.translated_name && med.translated_name !== med.name && (
                          <p className="text-xs text-gray-500">Original: {med.name}</p>
                        )}
                      </div>
                      <span className="text-xs bg-gray-200 px-2 py-1 rounded">#{String(index + 1).padStart(2, '0')}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-gray-500">Dosage:</span>
                        <span className="ml-1 font-medium">{med.dosage || "-"}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Qty:</span>
                        <span className="ml-1 font-medium">{med.quantity || "-"}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Frequency:</span>
                        <span className="ml-1 font-medium">{med.translated_frequency || med.frequency || "-"}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Duration:</span>
                        <span className="ml-1 font-medium">{med.translated_duration || med.duration || "-"}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Medication Notes/Warnings */}
              {medicationNotes.length > 0 && (
                <div className="border border-amber-300 bg-amber-50 rounded-lg p-4 mb-6">
                  <h3 className="text-xs font-semibold text-amber-700 uppercase mb-2">Pharmacy Notes</h3>
                  <ul className="text-sm text-amber-800">
                    {medicationNotes.map((note, idx) => (
                      <li key={idx} className="mb-1">• {note}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Notes */}
              {displayData.notes && (
                <div className="border border-gray-300 rounded-lg p-4 mb-6">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">Notes / Instructions</h3>
                  <p className="text-gray-700">{displayData.notes}</p>
                </div>
              )}

              {/* Footer */}
              <div className="border-t-2 border-gray-800 pt-4">
                <div className="flex justify-between items-center text-sm">
                  <div>
                    <p className="font-medium text-emerald-700">Valid for use in {getCountryName(displayData.destination_country)}</p>
                    <p className="text-gray-500">Present this document at any pharmacy</p>
                  </div>
                  <div className="text-right text-gray-500">
                    <p>EUMedTranslate</p>
                    <p className="text-xs">Cross-border prescription service</p>
                  </div>
                </div>
              </div>

              {/* Signature */}
              <div className="mt-8 pt-4">
                <div className="w-64">
                  {prescription.doctor_signature ? (
                    <div>
                      <img 
                        src={prescription.doctor_signature} 
                        alt="Doctor's signature" 
                        className="h-16 mb-1"
                      />
                      <div className="border-t border-gray-400 pt-1">
                        <p className="text-xs text-gray-500">Doctor's Signature</p>
                      </div>
                    </div>
                  ) : (
                    <div className="border-t border-gray-400 pt-2">
                      <p className="text-xs text-gray-500">Doctor's Signature</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      
      {/* Payment Modal for patients */}
      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        prescriptionId={prescription.id}
        onPaymentSuccess={() => {
          setHasAccess(true);
          setShowPaymentModal(false);
        }}
      />
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children, userType }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-700" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (userType && user.user_type !== userType) {
    return <Navigate to={user.user_type === "doctor" ? "/doctor" : "/patient"} />;
  }

  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-center" richColors />
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route
            path="/admin"
            element={
              <ProtectedRoute userType="admin">
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/doctor"
            element={
              <ProtectedRoute userType="doctor">
                <DoctorDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/doctor/pending"
            element={
              <ProtectedRoute userType="doctor">
                <DoctorPendingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/doctor/rejected"
            element={
              <ProtectedRoute userType="doctor">
                <DoctorRejectedPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/patient"
            element={
              <ProtectedRoute userType="patient">
                <PatientDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payment-success"
            element={
              <ProtectedRoute userType="patient">
                <PaymentSuccessPage />
              </ProtectedRoute>
            }
          />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
